// Compass data model. Mirrors the collections described in the project spec.

import type { Timestamp } from 'firebase/firestore';

export type ISODateString = string; // 'YYYY-MM-DD'

// ---- Multi-tenancy foundation (Phase 1, Stage A) -------------------------
// Compass is migrating from implicit single-tenancy (one council, one
// Firebase project, no tenant concept anywhere) to an explicit tenant
// model — see the multi-tenancy readiness audit + revised Phase 0/1
// architecture doc this shipped from. A tenant currently represents a
// Scouting America council; the architecture deliberately doesn't assume
// that stays true forever (hence generic `tenants`/`tenantId` vocabulary,
// not `councils`/`councilId`).
//
// Flat top-level collections stay flat — every tenant-owned document gets
// a `tenantId` field alongside its existing `districtId` (where it has
// one), rather than moving under /tenants/{tenantId}/... nesting. See the
// architecture doc for why (no collection-group queries exist today,
// composite indexes/rules extend the same way districtId already does,
// and a field preserves future cross-tenant product-level reporting that
// nesting would structurally block).
//
// Stage A (this pass): add tenantId to every tenant-owned type and every
// writer, WITHOUT changing Firestore authorization rules or migrating
// existing production documents. Existing behavior is unaffected — new
// documents simply start carrying an extra field nothing reads yet.

/** Immutable, system-generated tenant identifier — the join key for every
 *  tenant-owned document. Generated once in Stage A of the multi-tenancy
 *  migration; the real `tenants/{id}` document (Stage B) uses this exact
 *  value, so Stage A writers and Stage B's tenant record agree without a
 *  chicken-and-egg ordering problem. Not read from settings or config —
 *  a literal constant, exactly like this codebase's existing
 *  FROM_ADDRESS/APP_URL/COMMS_INBOUND_DOMAIN pattern (functions/src/
 *  email.ts, src/types.ts) for values that must match exactly across the
 *  client/Functions boundary. Mirrored verbatim in
 *  functions/src/tenant.ts — keep both in sync if this ever changes.
 *
 *  PERMANENT canonical Blackhawk production tenant ID — do not regenerate,
 *  do not derive from `slug`, do not create a second Blackhawk tenant ID.
 *
 *  This constant is Phase 1 migration/bootstrap scaffolding, NOT the
 *  permanent multi-tenant runtime design — it exists only because Stage A
 *  writers need a tenantId before any real tenant-selection mechanism
 *  exists. Once a second tenant is possible, code must stop assuming
 *  Blackhawk and instead resolve tenant context from (in order of
 *  preference): the authenticated user's active tenant membership, the
 *  `tenantId` already on the document being read/written, or an explicit
 *  tenant argument passed by the caller. Phase 2 must remove every
 *  remaining tenant-unfiltered assumption (the four automation engines
 *  included) before a second operational tenant can exist. */
export const BLACKHAWK_TENANT_ID = '8307f12f-c32a-4078-b21f-84cc82df7929';

/** tenants/{tenantId} — the canonical tenant entity. One document today
 *  (Blackhawk, created in Stage B); the architecture supports more
 *  without a schema change. `slug` is human-readable and independently
 *  mutable; `id`/tenantId itself never changes once created.
 *  `automationEnabled` is the Phase 1→2 safety gate: the four
 *  reconciliation engines stay internally single-tenant through Phase 1
 *  (there's only one tenant to be single about), but nothing should ever
 *  let a second tenant's automation run before those engines are
 *  genuinely tenant-filtered (Phase 2) — see the architecture doc's
 *  Section 10.
 *
 *  `accountOwnerUid` identifies the primary customer-side owner of this
 *  tenant's Compass account — the person who may later grant tenant
 *  administrative access to others. This is a distinct relationship from
 *  Summit Ave product ownership (see ProductMembership below): a Summit
 *  Ave employee's product role never implies they're a tenant's account
 *  owner, and vice versa. Exactly one intended account owner per tenant.
 *  Resolved to a real Firebase Auth UID during Stage B — never guessed;
 *  if the intended owner can't be uniquely resolved, Stage B stops and
 *  reports rather than writing this field. No transfer UI in Phase 1. */
export interface Tenant {
  id: string; // == tenantId — canonical machine identity, authorization/FK value
  /** Immutable, human-readable support/administrative identifier (e.g.
   *  "CMP-10001") — for support tickets, admin UI display, and humans
   *  talking about an account. NEVER a Firestore document ID, membership
   *  key, authorization boundary, or foreign key — `id`/tenantId remains
   *  the only value anything joins or authorizes against. No automatic
   *  sequencing in Phase 1 — assigned by hand at tenant creation. */
  accountId: string;
  slug: string;
  name: string;
  status: 'active' | 'suspended';
  automationEnabled: boolean;
  accountOwnerUid: string | null;
  createdAt?: Timestamp;
  updatedAt?: Timestamp;
}

/** tenantMemberships/{tenantId}__{uid} — one per (tenant, person). Every
 *  field here is exactly today's UserProfile shape (role/districtId/
 *  positions/committees/districtView/keyThree/councilWide/contactId),
 *  relocated, not redesigned — see the architecture doc's explicit
 *  instruction to preserve the existing meaning of every one of these.
 *  councilWide/keyThree/districtView are scoped to THIS tenant only, by
 *  construction: they only ever get consulted alongside a membership
 *  lookup for one specific tenantId, so there's no value that reaches
 *  across tenants the way a global flag could. */
export interface TenantMembership {
  id: string; // == `${tenantId}__${uid}`
  tenantId: string;
  uid: string;
  role: UserRole;
  districtId?: string | null;
  positions?: string[];
  /** Stable Permissions V2 assignments; V1 display names remain above for compatibility. */
  positionIds?: string[];
  committees?: string[] | null;
  districtView?: boolean | null;
  keyThree?: boolean | null;
  /** Legacy council-wide READ flag — superseded by `councilAccess` (Council
   *  Access Phase A). Kept on the doc for historical/recovery reference
   *  only; no Rule, query, or the Permissions V2 compiler reads it as an
   *  active authorization source anymore. Do not set this on a new
   *  membership — set `councilAccess` instead. */
  councilWide?: boolean | null;
  /** Council-level operational access, explicit per (tenant, membership) —
   *  never inferred from a Position or from org-chart placement. Ordered:
   *  'fullRW' implies 'fullRead' implies 'dashboardOnly'. Admin-settable
   *  only, via updateMembershipAccessV1. Independent of `contactAccess` —
   *  Full Read/RW never implies it. See
   *  docs/product-module-architecture.md's Council Access section (or the
   *  Phase A architecture report) for exact per-level semantics. */
  councilAccess?: 'dashboardOnly' | 'fullRead' | 'fullRW' | null;
  /** Protected contact-field (email/phone/notes) visibility, independent of
   *  `councilAccess` — Full Read/RW never implies this. Additive to
   *  whatever Position/relationship-based Full visibility a member already
   *  has (see contactVisibility.ts's hasBasicScope/projectContactForViewer;
   *  this is an extra OR branch, never a replacement). */
  contactAccess?: 'none' | 'read' | null;
  /** Compatibility pointer to the roster Contact this login was granted through. Being retired in favor of personId. */
  contactId?: string | null;
  /** Canonical pointer to the Person (tenantPeople) this login belongs to. */
  personId?: string | null;
  /** Denormalized COPY of the canonical UserIdentity (users/{uid}) fields
   *  of the same name — purely so Settings > Users & Access / Contacts can
   *  list/search members (and Key 3, who can't bulk-read uid-keyed
   *  identity docs — see firestore.rules' users/{userId} read rule) without
   *  a second per-row identity read. Not the source of truth: may lag a
   *  later self-service change on the identity doc itself (e.g. a
   *  provider-name backfill on next login) — acceptable staleness for a
   *  display/search convenience field, matching how contacts.name is
   *  similarly denormalized elsewhere in this codebase. */
  email?: string;
  displayName?: string | null;
  createdAt?: Timestamp;
  updatedAt?: Timestamp;
}

/** users/{uid} — global identity, auth linkage only. Firebase Auth UID is
 *  canonical (not email — a Firebase doc ID can't represent more than one
 *  record, which is exactly why email-as-doc-ID couldn't support a person
 *  belonging to more than one tenant). `tenantIds` is a denormalized
 *  index of active memberships for fast "which tenants" lookups; the
 *  authoritative membership data itself lives in TenantMembership.
 *  Deliberately does NOT carry role/district/positions — those are
 *  meaningless without a tenant context.
 *
 *  Legacy note (Stage A/B): the pre-existing `users/{email}` document
 *  (see UserProfile below) is NOT replaced or deleted by this — it stays
 *  as rollback-compatibility data through Phase 1. This interface
 *  describes the NEW, separate `users/{uid}` document Stage B creates
 *  alongside it. */
export interface UserIdentity {
  id: string; // == uid
  uid: string;
  email: string; // lowercased — attribute + lookup value, not the doc ID
  displayName?: string | null;
  /** Personal contact info — genuinely tenant-independent (same phone/
   *  notes regardless of which tenant this person is acting in today),
   *  matching the same "profile/contact info, not authorization" category
   *  as email/displayName (Phase 5C). Only used when there's no linked
   *  roster Contact for the active tenant — see TenantMembership.contactId. */
  phone?: string | null;
  notes?: string | null;
  tenantIds: string[];
  createdAt?: Timestamp;
  lastLoginAt?: Timestamp;
}

/** tenantInvites/{tenantId}__{emailKey} — Phase 5C's tenant-scoped
 *  replacement for using the legacy `users/{email}` doc as a pre-claim
 *  login grant. A person can't have a TenantMembership until they have a
 *  Firebase Auth UID, and the UID doesn't exist until they first sign in —
 *  so granting a login ahead of that first sign-in has to be keyed by
 *  email. The legacy plane did this with a SINGLE global email-keyed doc,
 *  which cannot represent "pending invite from Tenant A" and "pending
 *  invite from Tenant B" for the same email at once — exactly the
 *  email-as-security-boundary problem Phase 5C exists to remove. This
 *  collection fixes that by keying the pending grant to (tenant, email)
 *  instead of email alone. Fields are otherwise identical to
 *  TenantMembership's (the same role/district/positions shape, relocated
 *  again, not redesigned) plus displayName/contactId for prefill. On first
 *  login, functions/src/identityClaim.ts consumes every tenantInvite (and
 *  every remaining legacy users/{email} doc) matching the caller's email
 *  and turns each into a real users/{uid} + TenantMembership pair, then
 *  deletes the invite (or the legacy doc is simply left as claimed —
 *  see identityClaim.ts). */
export interface TenantInvite {
  id: string; // == `${tenantId}__${emailKey}`
  tenantId: string;
  email: string; // lowercased
  role: UserRole;
  districtId?: string | null;
  positions?: string[];
  /** Stable Permissions V2 assignments copied into the claimed membership. */
  positionIds?: string[];
  committees?: string[] | null;
  districtView?: boolean | null;
  keyThree?: boolean | null;
  /** Legacy — see TenantMembership.councilWide's doc comment. Not an active
   *  authorization source. */
  councilWide?: boolean | null;
  /** Carried into the real TenantMembership at claim time — see
   *  TenantMembership.councilAccess's doc comment for exact semantics. */
  councilAccess?: 'dashboardOnly' | 'fullRead' | 'fullRW' | null;
  /** Carried into the real TenantMembership at claim time — see
   *  TenantMembership.contactAccess's doc comment. */
  contactAccess?: 'none' | 'read' | null;
  contactId?: string | null;
  displayName?: string | null;
  /** Only meaningful for an admin invite (no districtId/roster Contact —
   *  see TenantMembership.contactId): for anyone with a district, this is
   *  captured on their linked Contact instead. Copied onto the new
   *  UserIdentity's phone/notes at claim time; ignored thereafter. */
  phone?: string | null;
  notes?: string | null;
  createdAt?: Timestamp;
  updatedAt?: Timestamp;
}

/** identityClaims/{uid}__{tenantId} — immutable audit record of a
 *  first-login claim into one tenant (see functions/src/identityClaim.ts).
 *  One per (person, tenant), written once, never overwritten — a claim can
 *  only ever happen once per tenant because the claim function's own fast
 *  path refuses to re-claim a (uid, tenantId) pair that already has a
 *  matching tenantMembership. Deliberately separate from UserIdentity/
 *  TenantMembership so those stay clean, matching this codebase's existing
 *  loginEvents-as-separate-audit-trail pattern. Admin-read-only; written
 *  only by the claim Cloud Function (Admin SDK), never by a client. */
export interface IdentityClaim {
  id: string; // == `${uid}__${tenantId}`
  uid: string;
  email: string;
  legacyDocId: string;
  tenantId: string;
  method: 'first-login-callable';
  claimedAt?: Timestamp;
}

/** productMemberships/{uid} — authorization to the future Summit Ave/TC
 *  Networks product-management application (`manage.compassgo.app`),
 *  deliberately separate from tenant admin (TenantMembership.role ===
 *  'admin'). This is a DIFFERENT relationship from tenant membership, not
 *  a stronger version of it: product-membership status does NOT grant
 *  access to any tenant's operational data by itself — a Summit Ave
 *  employee who needs to work inside a tenant still needs an explicit
 *  TenantMembership for it, same as anyone else. The same Firebase Auth
 *  UID may simultaneously hold a ProductMembership and one or more
 *  TenantMemberships (e.g. a Summit Ave employee who is also a council
 *  volunteer) — these never imply each other. No helper should treat
 *  role === 'owner'/'admin' here as automatic tenant access.
 *
 *  `manage.compassgo.app` itself is not built in Phase 1 — this is just
 *  the backend identity/authorization model Phase 1 needs so that future
 *  separation is possible without a schema change. Compass
 *  (`app.compassgo.app`), the public site (`compassgo.app`), and Manage
 *  may all share this Firebase project, Firebase Auth, and the global
 *  `users/{uid}` identity. Do not build the Manage UI or a Firebase
 *  Hosting site for it yet — see the architecture doc's explicit scope.
 *
 *  A small, explicit, deliberately-not-role-derived list of product
 *  members, kept as a real collection since they are added
 *  operationally rather than being a fixed, rarely-changing set. No UI in
 *  Phase 1 — see the architecture doc's explicit "do not build" scope. */
export interface ProductMembership {
  id: string; // == uid
  uid: string;
  role: 'owner' | 'admin';
  status: 'active' | 'suspended';
  createdAt?: Timestamp;
  updatedAt?: Timestamp;
}

/** tenants/{tenantId}/entitlements/{applicationKey}__{moduleKey} — customer
 *  entitlement architecture (see docs/product-module-architecture.md,
 *  Section 9). Answers "what may this tenant use," never "what may this
 *  user do" — a completely separate concern from TenantMembership/role.
 *
 *  `status` stores administrative intent, not a computed calendar state.
 *  The effective state a caller should actually act on is derived, not
 *  stored: `status === 'active' && (!effectiveFrom || today >=
 *  effectiveFrom) && (!effectiveUntil || today < effectiveUntil)`. A
 *  future-dated effectiveFrom already expresses "scheduled"; a passed
 *  effectiveUntil already expresses "expired" — neither needs its own
 *  stored status value, and nothing needs to run on a schedule to flip one.
 *
 *  Absence of a document for a given (tenant, application, module) IS the
 *  not-entitled state — never represented by a document with some
 *  "none"/"inactive" status.
 *
 *  `applicationKey`/`moduleKey` must be validated against
 *  packages/compass-product-registry before a document is written (done
 *  server-side, in the entitlement-writing Cloud Function — see
 *  functions/src/entitlements.ts). No client and no Compass Manage code
 *  writes this collection directly; the only write path is that Function.
 */
export type EntitlementStatus = 'active' | 'suspended';

/** How this entitlement came to exist — administrative/support context,
 *  never a differently-behaving runtime state. A trial-active module
 *  behaves identically to a paid-active one everywhere except in how
 *  support and Manage talk about it. 'subscription' is not yet produced by
 *  anything (subscription records are deferred); 'manual'/'trial' are the
 *  only sources Compass Manage's Phase 1 grant UI can produce, restricted
 *  to an active product owner/admin. */
export type EntitlementSource = 'subscription' | 'manual' | 'trial' | 'migration';

export interface ModuleEntitlement {
  id: string; // == `${applicationKey}__${moduleKey}`
  tenantId: string;
  applicationKey: string;
  moduleKey: string;
  status: EntitlementStatus;
  effectiveFrom?: ISODateString | null;
  effectiveUntil?: ISODateString | null;
  source: EntitlementSource;
  notes?: string | null;
  createdAt?: Timestamp;
  createdBy: string; // uid
  updatedAt?: Timestamp;
  updatedBy: string; // uid
}

/** entitlementAuditLog/{id} — immutable trail of every ModuleEntitlement
 *  change, separate from the entitlement document itself (same
 *  operational-doc/audit-log separation this codebase already uses for
 *  loginEvents next to users, and backupAuditLog next to its exports).
 *  Written only by the same Function that writes ModuleEntitlement, inside
 *  the same transaction. Admin/product-admin read-only; no client write
 *  path. Compass Manage's existing Audit screen is the intended
 *  destination for this data. */
export interface EntitlementAuditEntry {
  id: string;
  tenantId: string;
  applicationKey: string;
  moduleKey: string;
  changedBy: string; // uid
  changedAt?: Timestamp;
  previousValue: Omit<ModuleEntitlement, 'id'> | null; // null on first grant
  newValue: {
    status: EntitlementStatus;
    effectiveFrom: ISODateString | null;
    effectiveUntil: ISODateString | null;
    source: EntitlementSource;
    notes: string | null;
  };
  reason?: string | null;
}

/** Phase 5 — client-side mirror of functions/src/entitlementResolution.ts's
 *  response shape (the getEntitlementDiagnostics callable's return value).
 *  Observe-only: nothing in this codebase enforces on these values yet —
 *  see docs/product-module-architecture.md Section 9 and the Phase 5 plan.
 *  'scheduled'/'expired' are never stored on ModuleEntitlement itself;
 *  they're derived server-side by comparing effectiveFrom/effectiveUntil
 *  to today. */
export type ResolvedEntitlementStatus =
  | 'active'
  | 'scheduled'
  | 'expired'
  | 'suspended'
  | 'not_entitled'
  | 'unknown_module'
  | 'error';

export interface EntitlementResolution {
  tenantId: string;
  applicationKey: string;
  moduleKey: string;
  displayName: string;
  status: ResolvedEntitlementStatus;
  effectivelyActive: boolean;
  source: string | null;
  effectiveFrom: ISODateString | null;
  effectiveUntil: ISODateString | null;
  notes: string | null;
  error?: string;
}

export interface EntitlementDiagnosticsResult {
  tenantId: string;
  resolvedAt: string;
  modules: EntitlementResolution[];
}

/** Permission level. 'chair' vs 'member' is derived from the user's Position:
 *  a Position with `lead` set produces 'chair', otherwise 'member'. 'admin' is
 *  council-level and set directly. */
export type UserRole = 'admin' | 'chair' | 'member';

/** A district committee seat, configured by admins in Settings. A person's
 *  access is derived from the Position they hold. */
export interface Position {
  /** Stable identity used by policy, hierarchy, and administration. */
  positionId: string;
  name: string;
  /** One of settings.committees, or '' when the seat isn't tied to one. */
  committee: string;
  /** Operates across the whole district (all committees) rather than one. */
  districtWide: boolean;
  /** "Responsible for this sub-committee" — grants edit + task-create
   *  authority. Without it the holder can only work tasks assigned to them. */
  lead: boolean;
  /** Stable authoritative parent identity. Null means top-level. */
  reportsToPositionId: string | null;
  /** One of the district's Key 3 (District Chair / Commissioner / Executive).
   *  Rendered in the leadership band at the top of the org chart. */
  key3?: boolean;
  /** Council Organization Phase B. Absent/'district' (every Position that
   *  predates this field) means exactly what it always has: a per-district
   *  seat, held once per district. 'council' means a single council-wide
   *  seat, independent of any one district — see deriveAccess()'s doc
   *  comment for why a council Position is deliberately excluded from V1
   *  committee/chair derivation; its real authorization comes only through
   *  explicit Permissions V2 policy, keyed by positionId. `districtWide`
   *  has no meaning for a council Position (there's no set of "this
   *  district's committees" to span) and should stay false. */
  organizationLevel?: 'district' | 'council';
  /** Person-native Org Chart migration, Phase 2A. Absent/'single' (every
   *  Position that predates this field) means the normal case: at most one
   *  active holder per scope. 'multiple' means more than one Person may
   *  actively hold this seat in the same scope simultaneously (e.g. Unit
   *  Commissioner, Member-at-Large) — enforced server-side by
   *  positionAssignments.ts, which reads this value from the trusted
   *  Position catalog and never from a client request. Not yet surfaced
   *  in the Position editor UI or consumed by Org Chart rendering — see
   *  ARCHITECTURE.md for what's currently operational. */
  cardinality?: 'single' | 'multiple';
}

export type PriorityStatus = 'active' | 'archived';
export type GoalStatus = 'active' | 'completed' | 'archived';

/** An id-backed lookup record (districts, committees). Other docs reference
 *  `id`; `name` is display-only and safe to rename. */
export interface NamedRecord {
  id: string;
  name: string;
}

/** A committee, optionally nested under a parent (e.g. Camping under
 *  Program). '' or unset = top-level. See committeeDescendants().
 *
 *  Organization Model Phase C: `organizationLevel` makes Council and
 *  District two independent organizational-area vocabularies, not one
 *  shared namespace filtered by a UI toggle. A Council committee and a
 *  District committee may share a display name (e.g. both named
 *  "Program") but are distinct, independently configurable records with
 *  distinct ids — nesting must stay within the same level (enforced in
 *  Settings' Committees UI and positionConfigurationErrors()). Absent or
 *  'district' is fully backward-compatible: every committee that predates
 *  this field represents the one District organizational model shared by
 *  all Districts (there is no per-district committee configuration). */
export interface Committee extends NamedRecord {
  parentId?: string | null;
  organizationLevel?: 'council' | 'district';
}

/** Every committee id at or below `id` in the hierarchy, `id` itself
 *  excluded. Used to cascade a parent committee's chair access down to its
 *  sub-committees. `seen` guards against a cycle in hand-edited data. */
export function committeeDescendants(
  committees: Committee[],
  id: string,
  seen: Set<string> = new Set([id]),
): string[] {
  const children = committees.filter((c) => c.parentId === id && !seen.has(c.id));
  for (const c of children) seen.add(c.id);
  return children.flatMap((c) => [c.id, ...committeeDescendants(committees, c.id, seen)]);
}

/** A task status. `isComplete` drives done / not-overdue styling, so renaming
 *  "Done" doesn't break anything. */
export interface StatusRecord extends NamedRecord {
  isComplete: boolean;
}

export function newId(): string {
  return crypto.randomUUID();
}

/** Look up a record's display name by id; falls back to the id itself. */
export function recName(
  list: NamedRecord[] | undefined,
  id: string | null | undefined,
): string {
  if (!id) return '—';
  return (list ?? []).find((r) => r.id === id)?.name ?? id;
}

/** True when the given status id is a "complete" status. */
export function statusIsComplete(
  statuses: StatusRecord[] | undefined,
  id: string | null | undefined,
): boolean {
  return !!(statuses ?? []).find((s) => s.id === id)?.isComplete;
}

/** A council strategic-plan pillar / priority. Written by council leadership. */
export interface StrategicPriority {
  tenantId: string;
  id: string;
  title: string;
  description: string;
  programYear: string; // e.g. '2026-2027'
  status: PriorityStatus;
  createdBy: string; // user email key
  createdAt?: Timestamp;
  updatedAt?: Timestamp;
}

/** A measurable goal cascading from exactly one strategic priority. */
export interface Goal {
  tenantId: string;
  id: string;
  /** Human-readable id, e.g. 'GOL-004'. Assigned on create; display-only. */
  ref?: string;
  title: string;
  /** What council itself is doing/committing to for this goal. */
  councilDescription: string;
  /** What each district is expected to do — the day-to-day ask. */
  districtDescription: string;
  status: GoalStatus;
  createdBy: string;
  committee: string; // one of settings.committees
  districtIds: string[]; // districts this goal applies to; all 4 when council-wide
  strategicPriorityId: string;
  createdAt?: Timestamp;
  updatedAt?: Timestamp;
}

/** Where a task came from. 'goal' — the strategic-plan cascade. 'initiative' —
 *  a backwards-planned event (see Initiative). 'standalone' — a committee work
 *  item that belongs to neither. */
export type TaskSource = 'goal' | 'initiative' | 'standalone';

/** One entry in a task's running notes log. Notes are append-only — you add a
 *  line at a time, you don't edit a shared blob. */
export interface NoteEntry {
  /** Author — user email key. */
  by: string;
  /** When it was written. ISO string (client clock) — the doc's updatedAt is
   *  the server-authoritative time. */
  at: ISODateString | string;
  text: string;
}

/** The four tasks every initiative offers by default. Each one's real result
 *  is a field (or fields) on the Initiative, not on the task — the task is the
 *  work to fill it in, and can't be completed until it is. */
export type DefaultTaskKind = 'date' | 'location' | 'registration' | 'eventChair';

/** A task belonging to exactly one district. Parented by a goal, an initiative,
 *  or nothing — `source` says which, and the matching id is set. */
export interface Task {
  tenantId: string;
  id: string;
  /** Human-readable id, e.g. 'TSK-041'. Assigned on create; display-only. */
  ref?: string;
  /** Set when source === 'goal'. */
  goalId?: string | null;
  /** Set when source === 'initiative'. */
  initiativeId?: string | null;
  source: TaskSource;
  districtId: string;
  committee: string; // from the parent goal/initiative, stored for direct querying
  title: string;
  /** What this task is — a sentence or two. Editable by the task's owner. */
  description: string;
  status: string; // one of settings.taskStatuses
  /** Running notes, oldest first. Append-only. */
  noteLog?: NoteEntry[];
  /** @deprecated pre-noteLog single note blob; still read for old rows. */
  notes?: string;
  dueDate?: ISODateString | null;
  /** For initiative tasks: days relative to the event date. Null due date until
   *  the initiative's Date task sets `eventDate`, then computed from this. */
  offsetDays?: number | null;
  /** Free-form grouping bucket, e.g. from an event template step. */
  category?: string | null;
  /** Set on one of an initiative's four built-in tasks. Completion is blocked
   *  until the matching Initiative field is filled (see initiativeFieldReady). */
  defaultKind?: DefaultTaskKind | null;
  /** A Position name. Turnover-proof: resolves to whoever currently holds it. */
  assignedPosition?: string | null;
  /** One id for a person, several for a group. Mutually exclusive with assignedPosition in practice. */
  assignedContactIds?: string[] | null;
  /** Canonical Person-keyed assignees (Phase 2.3). Written by the client; assignedContactIds is derived by the server. */
  assignedPersonIds?: string[] | null;
  createdBy?: string;
  createdAt?: Timestamp;
  updatedAt?: Timestamp;
  updatedBy?: string;
}

// ---- Actions (Phase 1: manual only) -------------------------------------
// "Action Changes Things." A first-class work-management layer, distinct
// from a Goal Task: a Task is planned work beneath a Goal; an Action is
// work assigned to exactly one accountable person, optionally with
// participants, tracked through completion, and only loosely (optionally)
// related to a Goal/Initiative/Unit/person rather than owned by one.
//
// Phase 1 is manual-creation only (see `source`), but the schema is shaped
// for a later automated source (SYT/advancement/recharter/leadership) to
// create and update Actions without a rewrite — see `sourceType`/
// `sourceKey` below.

export type ActionStatus = 'open' | 'inProgress' | 'waiting' | 'completed' | 'cancelled';
export type ActionPriority = 'low' | 'normal' | 'high' | 'critical';

/** 'manual' is the only value Phase 1 ever writes. 'system' is reserved for
 *  a future automated generator — see ActionSourceType. */
export type ActionSource = 'manual' | 'system';

/** Only meaningful when source === 'system'. Named now so a future
 *  automation slots in without a schema change; nothing in Phase 1 sets
 *  these except by hand for testing. */
export type ActionSourceType =
  | 'goal'
  | 'initiative'
  | 'syt'
  | 'advancementStalled'
  | 'recharter'
  | 'leadership'
  | 'hazardousWeather';

export const ACTION_STATUS_LABELS: Record<ActionStatus, string> = {
  open: 'Open',
  inProgress: 'In Progress',
  waiting: 'Waiting',
  completed: 'Completed',
  cancelled: 'Cancelled',
};

/** Order shown in status pickers/filters. */
export const ACTION_STATUS_ORDER: ActionStatus[] = [
  'open',
  'inProgress',
  'waiting',
  'completed',
  'cancelled',
];

export const ACTION_PRIORITY_LABELS: Record<ActionPriority, string> = {
  low: 'Low',
  normal: 'Normal',
  high: 'High',
  critical: 'Critical',
};

export const ACTION_PRIORITY_ORDER: ActionPriority[] = ['critical', 'high', 'normal', 'low'];

/** A resolved status counts as "done" for overdue/active-work purposes —
 *  completed and cancelled behave the same for that, even though they're
 *  distinct outcomes for reporting. */
export function actionIsResolved(status: ActionStatus): boolean {
  return status === 'completed' || status === 'cancelled';
}

/** One entry in an Action's activity timeline — human notes and significant
 *  system-recorded workflow events interleaved, oldest first, append-only.
 *  Deliberately one merged stream (not a separate "notes" field) so the
 *  history reads as a single narrative — see addActionNote() /
 *  addActionActivity() in lib/db.ts. `text` is third-person and never
 *  repeats the actor's name — the UI renders `by`/`at` alongside it (same
 *  convention as Task's NoteEntry). */
export interface ActionActivityEntry {
  by: string; // user email key
  at: ISODateString | string;
  kind: 'note' | 'event';
  text: string;
}

/** Accountable work with exactly one owner. See the file header comment
 *  above for how this differs from a Task. */
export interface Action {
  tenantId: string;
  id: string;
  /** Human-readable id, e.g. 'ACT-014'. */
  ref?: string;
  title: string;
  description?: string;
  status: ActionStatus;
  priority: ActionPriority;
  /** Every Action belongs to exactly one district — same scoping model as
   *  Tasks/Contacts/Units, not a soft "related district" pointer. */
  districtId: string;

  /** Stable settings.committees id naming the responsible Subcommittee.
   * Server-controlled and independent of owner assignment. */
  subcommitteeId?: string | null;

  /** The one accountable person. Always a Contact id — never a Position
   *  (unlike Task's turnover-proof assignedPosition); an Action is
   *  deliberately assigned to a specific named person. */
  ownerContactId: string;
  /** Canonical owner (Phase 2.3). ownerContactId is '' for a Person with no roster Contact. */
  ownerPersonId?: string;
  /** Others who can view, comment, and see history — never jointly
   *  accountable. Always present (empty array, not omitted) once created. */
  participantContactIds: string[];
  participantPersonIds?: string[];

  dueDate?: ISODateString | null;

  /** 'manual' for everything Phase 1 creates. See ActionSource. */
  source: ActionSource;
  /** Set only when source === 'system' — null/absent for every Phase 1
   *  (manual) Action. */
  sourceType?: ActionSourceType | null;
  /** Reserved for future system-generated Actions: a deterministic key
   *  (e.g. `syt:<memberId>:<unitId>`) identifying the real-world condition
   *  an Action was generated for. Nothing in Phase 1 sets or reads this on
   *  the Action doc itself — uniqueness while ACTIVE is enforced by a
   *  companion `actionLocks/{sourceKey}` doc (see ActionLock below and the
   *  architecture note in lib/actions.ts / findOrCreateActionByKey() in
   *  lib/db.ts), not by querying this field, since a query-then-create can
   *  race. Once an Action with a given key is completed/cancelled, its
   *  lock is released and the key is free for a fresh Action if the
   *  condition recurs. */
  sourceKey?: string | null;

  /** Optional relationships — an Action requires none of these. */
  relatedUnitId?: string | null;
  /** The full set of related units, for an Action that's genuinely about
   *  more than one (e.g. a SYT follow-up for a leader who serves multiple
   *  units) — `relatedUnitId` above stays set to the first/primary one for
   *  anything that only ever looked at a single related unit. Absent or a
   *  single-element array for every Action type that's inherently
   *  one-unit (Advancement Stalled, manual Actions). */
  relatedUnitIds?: string[] | null;
  /** "Related person" — the Contact the Action is ABOUT (e.g. the leader
   *  whose SYT is expiring), distinct from who's doing the work
   *  (ownerContactId) or helping (participantContactIds). */
  relatedContactId?: string | null;
  relatedPersonId?: string | null;
  relatedGoalId?: string | null;
  relatedInitiativeId?: string | null;

  createdBy: string; // email
  createdAt?: Timestamp;
  updatedAt?: Timestamp;
  updatedBy?: string;
  completedAt?: Timestamp | null;

  /** Append-only. Always has at least the "created" event. */
  activity: ActionActivityEntry[];
}

/** A concurrency-safe uniqueness marker for one system-generated Action's
 *  `sourceKey`, doc id === the key itself (e.g. `syt:<memberId>:<unitId>`).
 *  Not read or written by anything in Phase 1 — reserved for Phase 2's
 *  automation, via findOrCreateActionByKey() in lib/db.ts. See that
 *  function and the architecture note in lib/actions.ts for why a
 *  lock-plus-transaction is used here instead of a
 *  query-for-existing-then-create, which can race under concurrent/
 *  retried automation runs. Deleted when the Action it points at resolves,
 *  freeing the key for a future recurrence of the same condition. */
export interface ActionLock {
  tenantId: string;
  id: string; // == the sourceKey
  actionId: string;
  createdAt?: Timestamp;
}

// ---- SYT automation (Actions Phase 2) -----------------------------------
// Written only by functions/src/sytAutomation.ts (Admin SDK) — see that
// file's header comment for the full engine design. Client-side types here
// are for reading the audit trail (Settings > SYT Automation), never for
// writing it — see firestore.rules' matching sytNotifications/
// sytReconciliationRuns blocks (write: false).

export type SytMilestone = '60day' | '30day' | 'expired' | `overdue-${number}`;
/** The notification-cadence milestone keys sytNotifications actually uses
 *  — distinct from SytMilestone (which is the Action's own 14-day
 *  escalation tier, unchanged). Leader overdue reminders follow a tapered
 *  cadence (14/28/42 days, then every 30) independent of that tier, so a
 *  notification's milestone is `overdue-r<index>`, never the raw
 *  `overdue-<n>` tier string — see functions/src/sytAutomation.ts's header
 *  comment for why these two are kept separate. */
export type SytNotificationMilestone = '60day' | '30day' | 'expired' | `overdue-r${number}`;
export type SytNotificationStatus = 'pending' | 'previewed' | 'sent' | 'partiallyFailed' | 'failed';

/** One resolved-and-recorded notification event for one SYT condition at
 *  one milestone — the doc id (`${sourceKey}__${milestone}`) IS the
 *  idempotency key, so "has this exact milestone already been handled?" is
 *  just "does this doc exist?". `status: 'previewed'` means the content was
 *  fully resolved and recorded but never actually sent, because
 *  SYT_LIVE_DELIVERY is off — see the engine's header comment. */
export interface SytNotification {
  tenantId: string;
  id: string;
  districtId: string;
  source: 'system';
  sourceType: 'syt';
  sourceKey: string; // syt:<memberId>
  milestone: SytNotificationMilestone;
  actionId: string | null;
  /** First/primary related unit, for back-compat with anything that only
   *  ever looked at a single unit — see relatedUnitIds for the full list. */
  relatedUnitId: string | null;
  relatedUnitIds?: string[] | null;
  relatedContactId: string | null;
  relatedMemberId: string;
  /** 'owner' = the district Action owner's one-time "Action assigned"
   *  email, sent once at Action creation — see sytAutomation.ts. */
  recipients: { name: string; email: string; role: 'leader' | 'key3' | 'owner' }[];
  channel: 'email';
  status: SytNotificationStatus;
  attemptedAt?: Timestamp;
  sentAt?: Timestamp | null;
  failure: string | null;
  createdAt?: Timestamp;
}

/** One run-summary doc per reconciliation execution (import-triggered,
 *  nightly, or admin manual/preview) — an operational log, not
 *  district-scoped, with tenant-admin client visibility. */
export interface SytReconciliationRun {
  tenantId: string;
  id: string;
  runAt: ISODateString;
  trigger: string;
  dryRun: boolean;
  stale: boolean;
  freshnessReportDate: ISODateString | null;
  conditionsScanned: number;
  conditionsSkippedIncompleteData: number;
  actionsCreated: number;
  actionsUpdated: number;
  notificationsSent: number;
  ambiguities: number;
  at?: Timestamp;
}

/** One row of a `sytAutomationPreview` callable's dry-run result — never
 *  stored in Firestore, just the shape of what the callable returns for the
 *  Settings > SYT Automation preview table. */
export type SytOwnerKind = 'trainingChair' | 'districtChairFallback' | 'unresolved';

export interface SytConditionOutcome {
  sourceKey: string;
  memberId: string;
  leaderName: string;
  /** Every unit this leader serves that's in scope for this condition — a
   *  leader serving multiple units gets ONE merged condition/Action, not
   *  one per unit (see functions/src/sytAutomation.ts). */
  unitNames: string[];
  districtId: string;
  tier: SytMilestone | null;
  actionWouldBeCreated: boolean;
  actionWouldBeUpdated: boolean;
  notificationWouldBeSent: boolean;
  resolvedOwnerContactId: string | null;
  resolvedOwnerKind: SytOwnerKind | null;
  resolvedOwnerName: string | null;
  ownerWouldBeReassigned: boolean;
  resolvedCommissionerContactIds: string[];
  resolvedRecipients: string[];
  blockingAmbiguity: string | null;
}

// ---- Generic brief-event journal (shared, not SYT-specific) -------------
// Written by any system automation (functions/src/briefEvents.ts) as a
// small, source-agnostic record of meaningful condition changes, so a
// future Weekly Compass Brief can summarize across automations without
// knowing any one automation's internal schema. See that file's header
// comment. No brief UI/aggregation reads this yet — client type exists for
// forward-readiness (Settings audit views, eventually the brief itself),
// mirroring SytNotification's own read-only client shape.

export type BriefEventState = 'upcoming' | 'due' | 'expired' | 'overdue' | 'resolved';

export interface BriefEvent {
  tenantId: string;
  id: string;
  category: string; // operational area, e.g. 'training'
  state: BriefEventState;
  sourceAutomation: string; // e.g. 'syt'
  sourceKey: string;
  milestoneKey: string;
  what: string;
  subjectName: string | null;
  subjectType: string | null;
  districtId: string;
  unitId: string | null;
  unitIds?: string[] | null;
  actionId: string | null;
  at?: Timestamp;
}

// ---- Advancement Stalled automation (Actions Phase 3) -------------------
// Written only by functions/src/advancementAutomation.ts (Admin SDK). Same
// shared shape/rules pattern as SYT's automation types above, minus any
// notification concept — this workflow never sends email.

export type AdvancementOwnerKind = 'advancementChair' | 'districtChairFallback' | 'unresolved';

export type AdvancementStatus =
  | 'notStalled'
  | 'gracePeriod'
  | 'actionWouldBeCreated'
  | 'existingAction'
  | 'wouldAutoComplete'
  | 'suppressedStale'
  | 'blockedOwnerUnresolved'
  | 'excludedUnitType'
  | 'noAdvancementData';

/** One run-summary doc per reconciliation execution — admin-only, same
 *  shape/purpose as SytReconciliationRun. */
export interface AdvancementReconciliationRun {
  tenantId: string;
  id: string;
  runAt: ISODateString;
  trigger: string;
  dryRun: boolean;
  unitsScanned: number;
  actionsCreated: number;
  actionsUpdated: number;
  autoCompleted: number;
  staleSuppressed: number;
  gracePeriod: number;
  ambiguities: number;
  at?: Timestamp;
}

/** One row of an `advancementAutomationPreview` callable's dry-run result —
 *  never stored in Firestore, just the shape of what the callable returns
 *  for the Settings > Advancement Automation preview table. */
export interface AdvancementConditionOutcome {
  unitId: string;
  unitName: string;
  districtId: string;
  unitType: string;
  youth: number | null;
  ranksYtd: number | null;
  reportDate: ISODateString | null;
  status: AdvancementStatus;
  actionWouldBeCreated: boolean;
  actionWouldBeUpdated: boolean;
  resolvedOwnerKind: AdvancementOwnerKind | null;
  resolvedOwnerName: string | null;
  resolvedCommissionerContactId: string | null;
  blockingAmbiguity: string | null;
}

// ---- Unit Renewal (Recharter) automation (Actions Phase 4) --------------
// Written only by functions/src/rechartAutomation.ts (Admin SDK). Same
// shared shape/rules pattern as SYT's automation types above — named
// "Recharter" internally to match Unit.recharter/UnitReportType's existing
// vocabulary; user-facing copy (email subjects, Action titles, Settings
// tab) says "Renewal," matching what the feature request itself calls it.

export type RechartMilestone = '90day' | '60day' | '30day' | 'expired' | `overdue-${number}`;
export type RechartOwnerKind = 'unitCommissioner' | 'districtCommissioner' | 'districtExecutiveFallback' | 'unresolved';

/** One run-summary doc per reconciliation execution — admin-only, same
 *  shape/purpose as SytReconciliationRun. */
export interface RechartReconciliationRun {
  tenantId: string;
  id: string;
  runAt: ISODateString;
  trigger: string;
  dryRun: boolean;
  stale: boolean;
  freshnessReportDate: ISODateString | null;
  unitsScanned: number;
  actionsCreated: number;
  actionsUpdated: number;
  notificationsSent: number;
  ambiguities: number;
  manuallyClosedSuppressed: number;
  reactivations: number;
  at?: Timestamp;
}

/** One row of a `rechartAutomationPreview` callable's dry-run result —
 *  never stored in Firestore, just the shape of what the callable returns
 *  for the Settings > Renewal Automation preview table. */
export interface RechartConditionOutcome {
  sourceKey: string;
  unitId: string;
  unitName: string;
  districtId: string;
  charterExpires: ISODateString;
  tier: RechartMilestone | null;
  actionWouldBeCreated: boolean;
  actionWouldBeUpdated: boolean;
  notificationWouldBeSent: boolean;
  resolvedOwnerContactId: string | null;
  resolvedOwnerKind: RechartOwnerKind | null;
  resolvedOwnerName: string | null;
  resolvedRecipients: string[];
  blockingAmbiguity: string | null;
  suppressedManualClose: boolean;
  reactivated: boolean;
}

// ---- Hazardous Weather Training automation -------------------------------
// Written only by functions/src/hazardousWeatherAutomation.ts (Admin SDK).
// Same shared shape/rules pattern as Advancement's automation types — no
// notification concept, this workflow never sends email either. See
// docs/hazardous-weather-training.md for the full architecture, including
// why 'expired' never actually occurs against today's real import data.

export type HazardousWeatherAutomationStatus = 'current' | 'due_soon' | 'expired' | 'missing' | 'not_applicable';
export type HazardousWeatherOwnerKind = 'trainingChair' | 'districtChairFallback' | 'unresolved';

/** One run-summary doc per reconciliation execution — admin-only, same
 *  shape/purpose as AdvancementReconciliationRun. */
export interface HazardousWeatherReconciliationRun {
  tenantId: string;
  id: string;
  runAt: ISODateString;
  trigger: string;
  dryRun: boolean;
  conditionsScanned: number;
  actionsCreated: number;
  actionsUpdated: number;
  autoCompleted: number;
  ambiguities: number;
  at?: Timestamp;
}

/** One row of a `hazardousWeatherAutomationPreview` callable's dry-run
 *  result — never stored in Firestore, just the shape of what the
 *  callable returns for the Settings > Hazardous Weather Automation
 *  preview table. */
export interface HazardousWeatherConditionOutcome {
  sourceKey: string;
  memberId: string;
  leaderName: string;
  unitNames: string[];
  districtId: string;
  status: HazardousWeatherAutomationStatus;
  actionWouldBeCreated: boolean;
  actionWouldBeUpdated: boolean;
  autoResolved: 'completed' | 'notApplicable' | null;
  resolvedOwnerKind: HazardousWeatherOwnerKind | null;
  resolvedOwnerName: string | null;
  ownerWouldBeReassigned: boolean;
  blockingAmbiguity: string | null;
}

// ---- events: templates + initiatives ------------------------------------
// Backwards-planning, adapted from the district DEs' "Backdater" workbook.
// A template is a reusable step list where each step carries an offset in days
// from the event. A district adopts a template as an Initiative (picks it,
// sets one real date), and tasks are generated with
// `dueDate = eventDate + offsetDays`. See src/lib/eventPlan.ts for the engine.

export type EventRecurrence = 'once' | 'monthly';

/** One reusable prep step within an EventTemplate. Stored inline on the
 *  template doc (a handful to a few dozen per template). */
export interface EventTemplateStep {
  /** Short imperative step, e.g. "Select chairman, recruit committee". */
  title: string;
  /** Grouping bucket for filtering/roll-up. Keep the vocabulary small. */
  category?: string;
  /** Days relative to the event: negative = before, 0 = event day, positive = after. */
  offsetDays: number;
  /** Position name (from settings.positions) to pre-assign this step to. */
  defaultPosition?: string;
  /** Optional longer guidance carried onto the generated task. */
  notes?: string;
}

/** The library entry. Council- or district-curated; adoption is opt-in per
 *  district (mirrors the workbook's "Used by <district>" column). */
export interface EventTemplate {
  tenantId: string;
  id: string;
  name: string;
  /** A committee id (settings.committees), or '' when not committee-scoped. */
  committee: string;
  recurrence: EventRecurrence;
  /** Provenance note, e.g. "Pioneer Backdater (2027)". */
  source?: string;
  /** Hidden from the adopt picker when false. */
  active: boolean;
  steps: EventTemplateStep[];
  /** Default applicability only; never Person assignments or Area names. */
  specializedContactAreaIds?: string[];
  createdBy?: string;
  createdAt?: Timestamp;
  updatedAt?: Timestamp;
}

export type InitiativeStatus = 'planning' | 'active' | 'complete' | 'archived';

/** One entry in an initiative's Changes log. Append-only audit trail — records
 *  a default task being dropped (with the required reason), the date/location/
 *  etc. being set, and status changes. */
export interface ChangeEntry {
  by: string; // user email key
  at: ISODateString | string;
  /** Human-readable, e.g. 'Dropped default task: Registration'. */
  action: string;
  /** Required when a default task is dropped; optional otherwise. */
  reason?: string;
}

/** One district running one event. Parents a set of tasks (source ===
 *  'initiative'). Created from an EventTemplate or hand-built (templateId null).
 *
 *  The event's key facts — date, location, registration link, chair — live
 *  here as fields. Each has a matching built-in task (the work to decide it);
 *  that task can't be completed until the field is filled. */
export interface Initiative {
  tenantId: string;
  id: string;
  /** Human-readable id, e.g. 'INIT-007'. Assigned on create; display-only. */
  ref?: string;
  districtId: string;
  /** The template this was adopted from, or null when hand-built. */
  templateId: string | null;
  name: string; // e.g. "Fishing Derby 2027"
  committee: string; // committee id, for scoping — like Goal/Task
  /** Optional link up to one strategic goal, when the event serves one. */
  strategicGoalId?: string | null;
  recurrence: EventRecurrence;
  /** 'council' — an admin applied this event across districts. 'district' — a
   *  district set it up itself. Absent on pre-rollout rows (treat as district). */
  origin?: 'council' | 'district';
  /** Shared id across the sibling initiatives created in one "apply to several
   *  districts" action. Null for a single-district initiative. */
  rolloutId?: string | null;

  // ---- key facts (set via the built-in tasks) ----
  /** Null until the Date task is done. First/only day of the event. */
  eventDate?: ISODateString | null;
  /** Last day, for a multi-day event. */
  eventEndDate?: ISODateString | null;
  /** Weather / fallback date. */
  backupDate?: ISODateString | null;
  allDay?: boolean;
  startTime?: string | null; // 'HH:MM'
  endTime?: string | null;
  /** Extra occurrence dates for a monthly initiative. */
  occurrenceDates?: ISODateString[];
  locationName?: string | null;
  locationAddress?: string | null;
  registrationUrl?: string | null;
  /** The one named Lead — a Position name, like Task.assignedPosition. */
  leadPosition?: string | null;
  /** Specific people leading this event (event chair(s)) — often not a standing
   *  position. Contact ids. */
  leadContactIds?: string[] | null;
  leadPersonIds?: string[] | null;

  /** Distinct specialized role, never a Contact fallback. Server-maintained. */
  specializedContacts?: Record<string, { personId: string | null }>;

  status: InitiativeStatus;
  notes?: string;
  changeLog?: ChangeEntry[];
  createdBy?: string;
  createdAt?: Timestamp;
  updatedAt?: Timestamp;
}

/** A district committee role-holder, or a non-committee volunteer. Replaces
 *  loose Scoutbook tracking. */
export interface Contact {
  tenantId: string;
  id: string;
  /** Human-readable id, e.g. 'PER-012'. Assigned on create; display-only. */
  ref?: string;
  /** null = a council-level Contact (no district). Only council viewers (admin, Council Access Full Read/RW) receive it. */
  districtId: string | null;
  /** Position names (from settings.positions) this person holds — a person
   *  can hold more than one ("wears multiple hats"). Empty for an ad-hoc
   *  helper with no standing seat. */
  positions: string[];
  /** Denormalized union of the held positions' committees, for roster
   *  grouping/queries — see deriveAccess(). Empty when none apply. */
  committees?: string[];
  /** Explicit trusted Contact-to-Unit associations. This is the only roster
   *  relationship that may compose with a Unit Commissioner's canonical
   *  unit assignment for Full Contact visibility. */
  unitIds?: string[];
  name: string;
  /** Present only when the server-authoritative visibility result is Full. */
  email?: string;
  phone?: string;
  notes?: string;
  /** Callable-delivery classification; absent on stored Firestore docs. */
  visibility?: 'basic' | 'full';
  /** The canonical Person this roster record is a compatibility view of. Server-owned: never written by the browser. */
  personId?: string | null;
  createdAt?: Timestamp;
  updatedAt?: Timestamp;
}

// ---- Qualifications (council-maintained specialized roles/certs) --------
// Organized by qualification AREA (NCAP, Shooting Sports, ...), not as one
// person-centric list — see docs on QualificationArea/QualificationAssignment
// below. Deliberately does NOT duplicate ordinary National training data
// Compass already imports (Trained Leaders/SYT/etc.) — this is for
// specialized, council-tracked certifications those imports don't cover.
// People are referenced by canonical Person ids only; nothing here ever
// writes to `contacts`.

/** One assignable area role within a QualificationArea (e.g. "Council NCAP
 *  Chair"). Distinct from a qualification: a role describes standing
 *  responsibility for the area, not a certification with a natural expiry —
 *  `requiresExpiration` exists as an escape hatch for the rare role that
 *  should track one, but stays false for the common case. This is also the
 *  seam a future phase would use to let an area role (e.g. "Council
 *  Shooting Sports Chair") carry scoped Compass authorization over just its
 *  own area, without touching every other Position — not implemented here,
 *  just not precluded by keeping roles a distinct, independently-queryable
 *  concept from qualifications. */
export interface QualificationRoleType {
  id: string;
  name: string;
  requiresExpiration?: boolean;
}

/** One assignable qualification/certification type within a
 *  QualificationArea (e.g. "Short-Term Camp Assessor"). Each type is
 *  independently grantable — see QualificationAssignment — so "Short-Term"
 *  and "Long-Term" are two separate types a person can hold in any
 *  combination, never a single-select field. */
export interface QualificationTypeDef {
  id: string;
  name: string;
}

/** A program area Qualifications is organized by (NCAP, Shooting Sports,
 *  ...). Holds the vocabulary of role/qualification types available within
 *  it — adding a new area, or a new role/qualification type within an
 *  existing area, is pure data (no new collection, component, or code path
 *  per area). Small, low-write-volume, admin-managed — same shape/edit
 *  pattern as AppSettings' positions/districts/committees, just its own
 *  collection rather than array fields on the settings singleton, since an
 *  area's assignments (QualificationAssignment) need their own doc identity
 *  to reference it by id. */
export interface QualificationArea {
  tenantId: string;
  id: string;
  name: string;
  active?: boolean;
  archived?: boolean;
  status?: string;
  /** Immutable product meaning; absent for custom council-created Areas. */
  systemKey?: 'ncap' | 'range_target_sports';
  description?: string;
  roleTypes: QualificationRoleType[];
  qualificationTypes: QualificationTypeDef[];
  createdAt?: Timestamp;
  updatedAt?: Timestamp;
}

/** One grant of a single area role or qualification to one person — the
 *  atomic "checkbox": holding both Short-Term and Long-Term Camp Assessor
 *  is two of these, not one doc with two fields. `expiresOn` is only ever
 *  meaningful for kind 'qualification' (or a 'role' whose
 *  QualificationRoleType.requiresExpiration is true) — absent/null means no
 *  expiration. This is the record a future event workflow queries via
 *  isQualificationValidOn() (lib/qualifications.ts) to answer "who holds a
 *  valid X qualification as of date D" — not wired to Initiatives yet, but
 *  this is the record that query will read. */
export interface QualificationAssignment {
  tenantId: string;
  id: string;
  areaId: string;
  personId: string;
  kind: 'role' | 'qualification';
  /** Matches a QualificationRoleType.id (kind 'role') or
   *  QualificationTypeDef.id (kind 'qualification') within the same area. */
  typeId: string;
  expiresOn?: ISODateString | null;
  createdAt?: Timestamp;
  updatedAt?: Timestamp;
  createdBy?: string;
  updatedBy?: string;
}

/** Qualifications-only allowlist, not a general Person directory. */
export interface QualificationPerson {
  personId: string;
  displayName: string;
  context?: string[];
  districtNames?: string[];
}

/** One sign-in into Compass — written by the client itself right after
 *  auth, immutable once created (see firestore.rules). "Login" here means
 *  each time the app starts a session for a signed-in user, which includes
 *  resuming an existing session on a page reload, not only an explicit
 *  click on "Sign in" — same moment the old lastLoginAt stamp fires. */
export interface LoginEvent {
  tenantId?: string | null; // optional — set where tenant context is known (Stage A)
  id: string;
  email: string; // lowercased
  displayName?: string | null;
  /** Firebase provider id, e.g. 'google.com' / 'microsoft.com'. */
  provider: string;
  at: Timestamp;
}

/** membershipAccessAuditLog/{autoId} — immutable audit record of a Council
 *  Access / Contact Access change on one tenant membership
 *  (Council Access Phase A). Create-only, like identityClaims —
 *  never edited or deleted, even by an admin. Written only by
 *  updateMembershipAccessV1 (Admin SDK); never a direct client write.
 *  Deliberately holds no Contact/donor payload — only the ACL field values
 *  themselves (none of which are PII), per the "don't log sensitive
 *  payloads unnecessarily" requirement. */
export interface MembershipAccessAuditEntry {
  id: string;
  tenantId: string;
  /** The membership that was changed — uid, not email, since this is
   *  post-claim membership administration. */
  targetUid: string;
  targetEmail: string;
  before: {
    councilAccess: 'dashboardOnly' | 'fullRead' | 'fullRW' | null;
    contactAccess: 'none' | 'read' | null;
  };
  after: {
    councilAccess: 'dashboardOnly' | 'fullRead' | 'fullRW' | null;
    contactAccess: 'none' | 'read' | null;
  };
  actorUid: string;
  actorEmail: string;
  reason?: string | null;
  at: Timestamp;
}

// ---- Communications (email blasts) ----------------------------------------
// Sent through the `sendCommunication` Cloud Function, which resolves the
// recipient list server-side and writes THREE docs per send so access can
// differ: `communications` (metadata — visible to the whole affected
// district's org tree, for accountability) and `communicationBodies` (the
// message text — returned only through a server authorization boundary) and
// `communicationRecipientAudits` (the complete recipient set — never readable
// by clients). The client never writes any of them; see firestore.rules.

export type CommScope =
  | 'allLeaders'
  | 'districtCommittee'
  | 'districtKey3'
  | 'unitKey3'
  | 'scoutsBsaLeaders'
  | 'cubLeaders';

export const COMM_SCOPE_LABELS: Record<CommScope, string> = {
  allLeaders: 'All Leaders',
  districtCommittee: 'District Committee',
  districtKey3: 'District Key 3',
  unitKey3: 'Unit Key 3s',
  scoutsBsaLeaders: 'Scouts BSA Leaders',
  cubLeaders: 'Cub Scout Leaders',
};

export type CommunicationTypeId =
  | 'general_announcement'
  | 'event_promotion'
  | 'roundtable'
  | 'training_program';

export interface CommunicationTypeOption {
  id: CommunicationTypeId;
  label: string;
  description: string;
}

/** Fixed sender for SPF/DKIM alignment — every blast is actually FROM this
 *  address; the compose "From" choice only sets the reply-to. Mirrors
 *  functions/src/email.ts's FROM_ADDRESS (the actual header value sent to
 *  Postmark) — kept as a bare address here since this is just UI display
 *  text, not a real From header. compassgo.app is DKIM/Return-Path
 *  verified in Postmark for SENDING; see COMMS_INBOUND_DOMAIN below for why
 *  that's a separate, unrelated domain. */
export const COMMS_FROM_ADDRESS = 'notifications@compassgo.app';

/** Sentinel `districtId` for a council-wide send (every district). */
export const COMMS_ALL_COUNCIL = 'ALL';

/** The domain Postmark's INBOUND stream parses mail for (per-initiative
 *  reply addresses, e.g. init-14@…) — deliberately NOT compassgo.app.
 *  compassgo.app's Postmark verification so far only covers SENDING
 *  (DKIM/Return-Path); inbound parsing needs its own MX records pointed at
 *  Postmark, which hasn't been set up for compassgo.app. Moving this to
 *  compassgo.app before that exists would silently break initiative email
 *  replies. Must match functions/src/inbound.ts's own INBOUND_DOMAIN
 *  constant. */
export const COMMS_INBOUND_DOMAIN = 'compass.blackhawkscouting.org';

/** The inbound address for an initiative — its lowercased ref @ the comms
 *  domain, e.g. INIT-014 -> "init-14@compass.blackhawkscouting.org". Null for
 *  a pre-ref initiative. Mail sent here is logged on the initiative by the
 *  `inboundEmail` Cloud Function. */
export function initiativeInboundAddress(ref?: string | null): string | null {
  return ref ? `${ref.toLowerCase()}@${COMMS_INBOUND_DOMAIN}` : null;
}

/** An email received at an initiative's address (via Postmark inbound).
 *  Written only by the `inboundEmail` Cloud Function; see firestore.rules. */
export interface InitiativeMessage {
  tenantId: string;
  id: string;
  initiativeId: string;
  initiativeRef: string; // 'INIT-014'
  districtId: string; // denormalized for the read rule
  fromEmail: string;
  fromName: string;
  subject: string;
  /** The reply portion (Postmark's StrippedTextReply) when available, else the
   *  full plain-text body. */
  text: string;
  /** File names only — attachments are not stored in v1. */
  attachmentNames: string[];
  /** Postmark's MessageID — dedupe key for webhook retries. */
  postmarkMessageId: string;
  receivedAt: Timestamp;
}

export interface Communication {
  tenantId: string;
  id: string;
  sentAt: Timestamp;
  sentByEmail: string;
  sentByName: string;
  replyTo: string;
  /** A settings.districts id, or COMMS_ALL_COUNCIL. */
  districtId: string;
  /** null when districtId === COMMS_ALL_COUNCIL. */
  scope: CommScope | null;
  audienceLabel: string; // "Sycamore District Committee"
  /** Canonical delivery metadata added after Communication Types launched.
   *  Absent on legacy records; never infer it from message content. */
  communicationTypeId?: CommunicationTypeId;
  communicationTypeLabel?: string;
  deliveryClassification?: 'broadcast';
  postmarkMessageStream?: string;
  communicationTypeRegistryVersion?: string;
  subject: string;
  recipientCount: number;
  status: {
    /** Legacy pre-type delivery counters. */
    sent?: number;
    /** Actual asynchronous bounces when known; legacy rows used this for all rejections. */
    bounced: number;
    accepted?: number;
    suppressed?: number;
    otherRejected?: number;
    /** Recipients in a network-interrupted chunk whose provider acceptance
     * could not be proven; Compass will not automatically resend them. */
    uncertainRecipients?: number;
    /** Preview mode: how many recipients the message was addressed to but NOT delivered to. */
    previewedRecipients?: number;
    providerErrors?: Array<{ errorCode: number; count: number }>;
  };
  deliveryState?: 'sending' | 'complete' | 'needs_attention' | 'previewed';
  /** True when the council was in preview mode: the communication was recorded and previewed but NOT delivered to anyone.
   *  Absent on records made before the outbound-email safeguard; those were real sends. */
  previewOnly?: boolean;
  transport?: {
    provider: 'postmark';
    endpoint: '/email/batch';
    batchCount: number;
    successfulBatches: number;
    failedBatches: number;
    uncertainBatches: number;
    pendingBatches: number;
  };
  /** Trusted server-side capability provenance for sends created after
   * Permissions V2 Increment 2G. Optional for pre-2G history rows. */
  authorization?: {
    model: 'permissions-v2';
    capabilityKey: 'communications.send';
    policyRevisionId: string;
    policyFingerprint: string;
    membershipFingerprint: string;
    effectiveScope: { kind: 'tenant'; tenantId: string } | { kind: 'district'; districtId: string };
    authenticatedUid: string;
  };
  createdAt: Timestamp;
}

export interface CommunicationBody {
  tenantId: string;
  id: string; // same id as the Communication
  body: string;
}

// ---- Unit Health (imported district unit data) ------------------------
// Populated by admin CSV imports of my.Scouting / Scoutbook reports — see
// lib/unitReports.ts (parsers) and lib/units.ts (matching + write). Units
// are district-scoped like tasks/contacts. Recharter and Key-3 data are
// current-state (overwritten each import); advancement is append-only so
// pace over the year can be charted.

export type UnitType = 'pack' | 'troop' | 'crew' | 'ship' | 'post' | 'club' | 'other';

export type UnitReportType =
  | 'recharter'
  | 'charterOrg'
  | 'key3'
  | 'advancementCub'
  | 'advancementScoutsBSA'
  | 'trainedLeaders'
  | 'syt'
  | 'commissionerAssignment'
  | 'connectionsHistory';

/** Worst Safeguarding Youth Training status across a unit's Key 3. (BSA
 *  renamed "Youth Protection Training" / YPT to "Safeguarding Youth
 *  Training" / SYT; the Key 3 Contact report's column is still labelled
 *  "YPT Status", so the field keys below keep the `ypt` spelling — only
 *  user-facing text says SYT.) */
export type YptStatus = 'active' | 'expiring' | 'expired' | 'unknown';

export interface UnitKey3Person {
  name: string;
  position: string; // "Committee Chair" / "Cubmaster" / "Scoutmaster" / …
  email: string;
  phone: string;
  ypt: YptStatus;
  yptRaw: string; // the report's own text, e.g. "Expires 61-90d"
}

export interface Unit {
  tenantId: string;
  id: string;
  districtId: string;
  /** my.Scouting unit id — present once an advancement report has been
   *  imported for this unit; the match key when it is. Name-only reports
   *  (recharter, Key 3) match on districtId + normalizedName. */
  scoutbookUnitId?: string | null;
  name: string; // "Pack 0011 (F)"
  normalizedName: string; // "PACK 0011 (F)" — match key
  type: UnitType;
  charterOrg?: string | null;

  /** Current recharter status — overwritten on each recharter import. */
  recharter?: {
    charterExpires: ISODateString;
    paidYouth: number;
    paidAdults: number;
    scoutLifeYouth: number;
    scoutLifeAdults: number;
    reportDate: ISODateString;
    importedAt: Timestamp;
  } | null;

  /** Current Key 3 + YPT — overwritten on each Key 3 import. */
  key3?: {
    hasCommitteeChair: boolean;
    hasUnitLeader: boolean; // Cubmaster / Scoutmaster / Advisor / Skipper
    hasCharterOrgRep: boolean;
    worstYpt: YptStatus;
    people: UnitKey3Person[];
    reportDate: ISODateString;
    importedAt: Timestamp;
  } | null;

  /** Chartered organization detail — overwritten on each Chartered
   *  Organizations Report import. `charterOrg` above (the name) is still
   *  the field every other report backfills; this carries everything else
   *  only this report has. */
  charterOrgDetail?: {
    orgTypeCode: string | null; // "008"
    orgType: string | null; // "Lutheran Churches"
    orgTypeShort: string | null; // "CHUR"
    address: string | null;
    cityState: string | null;
    zip: string | null;
    phone: string | null;
    tenureMonths: number | null;
    genderAccepted: string | null; // raw F/B/G/blank from the report
    expiryDate: ISODateString | null;
    paidYouth: number;
    paidAdults: number;
    specialInterestCode: string | null;
    specialInterestType: string | null;
    cisCouncilPaid: boolean;
    reportDate: ISODateString;
    importedAt: Timestamp;
  } | null;

  /** Latest advancement per program — denormalized from
   *  `unitAdvancementSnapshots` at import time so screens don't scan the
   *  (append-only, ever-growing) snapshot collection. The snapshots stay for
   *  history/reporting; `latestAdvancement` is what the app reads. Overwritten
   *  only when a newer report comes in. */
  latestAdvancement?: {
    cub?: UnitAdvancementLatest | null;
    scoutsBSA?: UnitAdvancementLatest | null;
  } | null;

  /** The Contact (holding the "Unit Commissioner" position) currently
   *  assigned to this unit, or null/absent if unassigned. Simple current-
   *  state assignment only — no history of past assignments. Written only
   *  by the District Commissioner (see the Commissioners page and
   *  firestore.rules' isDistrictCommissioner()). */
  commissionerContactId?: string | null;
  /** Canonical commissioner (Phase 2.3). */
  commissionerPersonId?: string | null;

  /** Current-state commissioner-visit summary, from either BSA Connections
   *  report — both carry the identical Metric_Summary figure (confirmed
   *  byte-for-byte against a real export), so whichever import runs last
   *  refreshes this. Overwritten each import, not a history — the full
   *  visit log lives in `unitConnections`. */
  commissionerActivity?: {
    metricsMet: number;
    /** "Last_Connection_Date" — the most recent commissioner connection on
     *  file, regardless of category. This is what the Commissioners page
     *  shows as "last visit." */
    lastConnectionDate: ISODateString | null;
    /** "Last_Contact" + "Last_Assessment_Score" — a separate, much less
     *  common formal unit-assessment visit, distinct from a routine
     *  connection (My.Scouting Commissioner Tools tracks these as two
     *  different things). Kept apart rather than merged into
     *  lastConnectionDate/metricsMet so neither figure is misrepresented. */
    lastAssessmentDate: ISODateString | null;
    lastAssessmentScore: number | null;
    reportDate: ISODateString;
    importedAt: Timestamp;
  } | null;

  /** Suggested Commissioner from the BSA Connections "Assigned/Unassigned"
   *  report — deliberately separate from `commissionerContactId` (the
   *  manual assignment on the Commissioners page) so an import can never
   *  silently overwrite what a human assigned. Matched to a real Contact
   *  by name only for display convenience — `name`/`memberId` are kept
   *  even when no Contact matches, so a mismatch can still be surfaced. */
  commissionerImportSuggestion?: {
    name: string | null; // null when the import itself shows the unit unassigned
    memberId: string | null;
    registrationExpired: boolean;
    unitExpired: boolean;
    unitExpiredDate: ISODateString | null;
    reportDate: ISODateString;
    importedAt: Timestamp;
  } | null;

  createdAt?: Timestamp;
  updatedAt?: Timestamp;
}

/** Where a commissioner-to-unit relationship came from: designated by hand in Compass, recorded from the Scouting America
 *  Assigned/Unassigned Commissioners report, or both. */
export type CommissionerOrigin = 'manual' | 'scoutingAmerica';

/** A commissioner who serves a unit. Any number per unit. References only: the Person is resolved to a display name through the
 *  Person-label lookup, and no contact detail is copied here. One document per commissioner and unit. Written by the server only.
 *
 *  A relationship is informational. It never grants access and never routes an Action. The ONE operational commissioner of a unit
 *  is `Unit.commissionerPersonId` (+ the legacy `commissionerContactId`), changed only by the server operation
 *  setUnitOperationalCommissionerV1; a relationship with the `manual` origin is how that designation is recorded here. */
export interface UnitCommissionerAssignment {
  id: string;
  tenantId: string;
  unitId: string;
  scoutbookUnitId: string | null;
  personId: string;
  memberId: string | null;
  districtId: string;
  /** Catalog positions held for this unit (e.g. pos_unit_commissioner); empty for a hand designation with no report behind it. */
  positionIds: string[];
  /** The positions exactly as the report printed them. */
  reportPositions: string[];
  /** ISO date the commissioner's registration in this position expires (from the report). */
  positionExpiry: string | null;
  status: 'active' | 'ended';
  /** Absent on relationships written before origins existed; those all came from the report. */
  origins?: CommissionerOrigin[];
  source?: string;
  lastSeenReportDate: string | null;
}

/** One append-only per-unit historical observation from a Recharter
 *  import — the authoritative per-unit youth/adult counts and charter
 *  expiration date that `Unit.recharter` above otherwise only keeps the
 *  LATEST of (overwritten on each import — see that field's own comment).
 *  Written alongside `Unit.recharter`'s overwrite and the existing
 *  district-level `MembershipSnapshot` aggregate (see `importUnitReport()`
 *  in lib/units.ts) — this is ADDITIVE per-unit history; it changes neither.
 *
 *  One doc per (tenant, reportDate, unit) — doc id built with
 *  `buildSnapshotId()` (lib/units.ts), the same deterministic-id pattern
 *  `UnitLeaderSnapshot` uses, so a same-date re-import overwrites this
 *  unit's one doc for that date rather than duplicating it.
 *
 *  Deliberately stores `charterExpires` raw, never a derived status label
 *  ("soon"/"overdue"/"current") — that label already depends on "compared
 *  to what date," so computing and freezing it now would silently go stale
 *  the moment someone views this history later; recompute it from
 *  `charterExpires` against whatever reference date the reader needs,
 *  exactly as the live Unit Health screen already does for the current
 *  state. */
export interface UnitRecharterSnapshot {
  tenantId: string;
  id: string;
  unitId: string;
  districtId: string;
  reportDate: ISODateString;
  /** The triggering unitImports doc id, for provenance — same convention
   *  as UnitAdvancementSnapshot.importId. */
  importId: string;
  importedAt: Timestamp;
  charterExpires: ISODateString;
  paidYouth: number;
  paidAdults: number;
  scoutLifeYouth: number;
  scoutLifeAdults: number;
}

/** One append-only per-unit historical observation from a Key 3 import —
 *  `Unit.key3` above otherwise only keeps the LATEST state (overwritten on
 *  each import). Mirrors `Unit.key3`'s own shape exactly, including the raw
 *  per-seat `people` list, so a future trend view can show not just
 *  "was the seat filled" but who held it and their YPT status as of that
 *  date, without needing a second collection or re-deriving anything.
 *
 *  One doc per (tenant, reportDate, unit); same `buildSnapshotId()`
 *  idempotency pattern as UnitRecharterSnapshot above. */
export interface UnitKey3Snapshot {
  tenantId: string;
  id: string;
  unitId: string;
  districtId: string;
  reportDate: ISODateString;
  importId: string;
  importedAt: Timestamp;
  hasCommitteeChair: boolean;
  hasUnitLeader: boolean;
  hasCharterOrgRep: boolean;
  worstYpt: YptStatus;
  people: UnitKey3Person[];
}

/** One commissioner connection/visit — from the BSA Connections History
 *  report. Append-only: a re-import must never lose prior visits, so the
 *  doc id is the report's own stable `Connection_ID`, making a re-import
 *  naturally idempotent (upsert, not insert) rather than needing a
 *  separate dedupe query. Rows with no Connection_ID (a unit that's never
 *  had a connection logged) aren't real visits and produce no doc here. */
export interface UnitConnection {
  tenantId: string;
  id: string; // == the report's Connection_ID
  unitId: string; // Compass Unit doc id
  districtId: string; // denormalized for scoping
  connectionDate: ISODateString | null;
  completeDate: ISODateString | null;
  status: string; // "Complete" / "InProgress" / … — raw from the report
  loggedByName: string;
  loggedByMemberId: string;
  /** Metric_Summary as of this connection — denormalized so the detail
   *  view doesn't need to cross-reference the unit doc's current value. */
  metricsMet: number;
  /** Free-text notes by category — only categories with real text are
   *  included. Category names are the report's own section labels. */
  comments: { category: string; text: string }[];
  reportDate: ISODateString;
  importedAt: Timestamp;
}

/** The current-state slice of an advancement snapshot, kept on the unit doc. */
export interface UnitAdvancementLatest {
  reportDate: ISODateString;
  programYear: string;
  youth: number;
  ranksYtd: number;
  eaglesYtd: number;
  meritBadgesYtd: number;
  byRank: Record<string, number>;
  importedAt: Timestamp;
}

/** One advancement snapshot for a unit — append-only, one per program +
 *  import date, so the year's pace can be charted. */
export interface UnitAdvancementSnapshot {
  tenantId: string;
  id: string;
  unitId: string; // Compass Unit doc id
  districtId: string; // denormalized for scoping
  program: 'cub' | 'scoutsBSA';
  /** Calendar year the YTD counts belong to, from the report's date window. */
  programYear: string; // "2026"
  importId: string;
  reportDate: ISODateString;
  importedAt: Timestamp;
  youth: number;
  ranksYtd: number;
  eaglesYtd: number; // 0 for cub
  meritBadgesYtd: number; // 0 for cub
  /** { lion: 2, tiger: 5, … } or { scout: 1, …, eagle: 1 }. */
  byRank: Record<string, number>;
}

/** Metadata for one CSV import — drives the "last imported" line.
 *
 *  `unmatchedCount` (Trained Leaders) is the SUM of two distinct failure
 *  kinds, broken out separately below so an admin can tell which one they
 *  have: a row the parser itself couldn't place (bad district/member/
 *  position data) vs. a row that parsed fine but whose unit name doesn't
 *  match any Compass Unit doc (unit-resolution/linking failure — see
 *  UnitLeaderSnapshot below and importTrainedLeaders() in lib/units.ts).
 *  Before this fix, unmatchedCount only ever reflected the first kind —
 *  unit-linking failures were silently invisible in the audit trail. */
export interface UnitImport {
  tenantId: string;
  id: string;
  reportType: UnitReportType;
  reportDate: ISODateString;
  importedAt: Timestamp;
  importedBy: string;
  rowCount: number;
  matchedCount: number;
  newUnitCount: number;
  unmatchedCount: number;
  fileName?: string | null;
  /** Trained Leaders only — the parser-level share of unmatchedCount. */
  unmatchedParseCount?: number;
  /** Trained Leaders only — the unit-resolution/linking share of
   *  unmatchedCount: a real, parsed leader row whose unit name didn't
   *  match any Compass Unit doc. */
  unmatchedUnitLinkCount?: number;
  /** Trained Leaders only — whether this import produced
   *  unitLeaderSnapshots history. The CURRENT-STATE unitLeaders roster is
   *  always written regardless of this flag; only the HISTORICAL record is
   *  ever withheld, and only when the import fails the completeness/trust
   *  safeguards in importTrainedLeaders() (see evaluateSnapshotTrust()). */
  snapshotCreated?: boolean;
  /** Set only when snapshotCreated is false — why history was skipped for
   *  this import. Never a reason to distrust the current-state roster. */
  snapshotSkippedReason?: string | null;
  /** Set only when snapshotCreated is true — this import's own row count,
   *  read back by the NEXT import's row-count-drop safeguard so that check
   *  doesn't need to scan the (potentially large) unitLeaderSnapshots
   *  collection just to find a comparison baseline. */
  snapshotRowCount?: number | null;
}

export const UNIT_REPORT_LABELS: Record<UnitReportType, string> = {
  recharter: 'Recharter',
  charterOrg: 'Chartered Organizations',
  key3: 'Key 3 / SYT',
  advancementCub: 'Cub advancement',
  advancementScoutsBSA: 'Scouts BSA advancement',
  trainedLeaders: 'Trained Leaders',
  syt: 'Safeguarding Youth Training',
  commissionerAssignment: 'Commissioner Assignments',
  connectionsHistory: 'Commissioner Connections',
};

/** Order the reports are listed in the import checklist. */
export const UNIT_REPORT_ORDER: UnitReportType[] = [
  'recharter',
  'charterOrg',
  'key3',
  'trainedLeaders',
  'syt',
  'commissionerAssignment',
  'connectionsHistory',
  'advancementCub',
  'advancementScoutsBSA',
];

/** Where each CSV comes from — shown in the import checklist so an admin
 *  knows exactly which export to pull. Adjust the wording to match the
 *  current my.Scouting / Scoutbook menus. */
export const UNIT_REPORT_SOURCES: Record<UnitReportType, string> = {
  recharter: 'my.Scouting → Roster → Reports → "Units Due to Recharter"',
  charterOrg: 'my.Scouting → Roster → Reports → "Chartered Organizations Report"',
  key3: 'my.Scouting → Roster → Reports → "Key 3 Contact Report"',
  trainedLeaders: 'my.Scouting → Training Manager → Reports → "Trained Leaders Status"',
  syt: 'my.Scouting → Training Manager → Reports → "Safeguarding Youth Training Aging Report"',
  advancementCub: 'my.Scouting → Roster → Reports → "Detailed Advancement Report — Cub Scouts"',
  advancementScoutsBSA: 'my.Scouting → Roster → Reports → "Detailed Advancement Report — Scouts BSA"',
  commissionerAssignment: 'my.Scouting → Commissioner Tools → Reports → "Assigned / Unassigned Report"',
  connectionsHistory: 'my.Scouting → Commissioner Tools → Reports → "Connections History Report"',
};

// ---- Data Imports — Import Management (Settings) ------------------------
// Ownership/accountability + reminder CONFIGURATION for the imports above.
// Deliberately a separate doc/collection from AppSettings (`settings/app`):
// this is admin-only in BOTH directions (read and write), unlike every
// existing `tenants/{tenantId}/settings/{docId}` doc, which any signed-in
// tenant member can already read under firestore.rules' generic
// `match /tenants/{tenantId}/settings/{docId}` block. Putting responsible-
// person identities and reminder configuration under that existing
// wildcard would make them readable by any ordinary Compass user via a
// direct Firestore query, not just hidden from the UI — which would
// violate "Do not expose administrative configuration through the
// read-only status UI." So this lives at `tenants/{tenantId}/
// dataImportAdmin/config` instead, with its own dedicated, strictly
// admin-only firestore.rules block — see the matching comment on that
// block in firestore.rules, and lib/db.ts's dataImportAdminRef()/
// saveDataImportAdminSettings() for the exact read/write shape.
//
// The responsible person is an owner/contact ONLY — it never gates who
// may perform an import. That permission remains exactly what it always
// was: any tenant member with role 'admin' (see firestore.rules' isAdmin()
// and the existing "Import data" gating in Unit Health/Settings).

/** Per-report override — every field is OPTIONAL and independently
 *  overridable; an absent field means "inherit the tenant-level default"
 *  for that one field specifically (see effectiveReportImportSettings()
 *  in lib/dataImports.ts). Resetting a report to defaults deletes this
 *  entire object (not merely blanks its fields), via lib/db.ts's
 *  resetDataImportReportOverride(). */
export interface DataImportReportOverride {
  responsiblePersonUid?: string | null;
  reminderDays?: number;
  reminderEnabled?: boolean;
}

/** The most recent import ATTEMPT for one report type — success or
 *  failure, always exactly one per report type (overwritten each time,
 *  never a growing log). This is deliberately separate from the
 *  `unitImports` collection, which only ever represents SUCCESSFUL
 *  imports and must stay that way — see importTrainedLeaders()'s trust
 *  safeguards and every other importer in lib/units.ts, none of which
 *  write anything to Firestore on failure today. A failed attempt here
 *  NEVER updates `unitImports`, never resets any freshness clock, and
 *  never produces an "Imported Today" indicator — those all still come
 *  exclusively from `unitImports`. */
export interface DataImportAttempt {
  status: 'success' | 'failed';
  at: Timestamp;
  by: string; // email of whoever attempted the import
  fileName: string | null;
  error: string | null;
}

/** `tenants/{tenantId}/dataImportAdmin/config` — one doc per tenant. */
export interface DataImportAdminSettings {
  tenantId: string;
  defaultResponsiblePersonUid: string | null;
  defaultReminderDays: number;
  /** RETIRED. Kept in the record so nothing is lost, but no longer read or shown: reminder emails are governed by the
   *  council's Administrative Notifications control (server-enforced). */
  emailRemindersEnabled: boolean;
  overrides: Partial<Record<UnitReportType, DataImportReportOverride>>;
  lastAttempt: Partial<Record<UnitReportType, DataImportAttempt>>;
  updatedAt?: Timestamp;
}

// The default reminder-day count (DEFAULT_DATA_IMPORT_REMINDER_DAYS) lives
// in lib/dataImports.ts, not here — same "small feature-local constant
// belongs with its evaluator, not in the shared types file" precedent as
// HAZARDOUS_WEATHER_DUE_SOON_DAYS in lib/unitHealth.ts. Keeping it out of
// this file specifically also avoids types.ts needing any REAL (non-type)
// export at all, which is what lets every pure src/lib/*.ts evaluator
// import from '../types' directly under plain node (no bundler) for
// direct-import test scripts — see dataImports.ts's own header comment.

// ---- Trained Leaders (from the "Trained Leaders Status" report) --------
// Every registered adult in the council, one Compass doc per person per
// scope (a person who's both a Scoutmaster and a district member gets two
// docs). Reference data only — NOT the district-committee roster
// (`contacts`); it feeds the leader-search on Add User and Org Chart
// assignment. Wiped and rewritten whole on each import.

export type LeaderScope = 'council' | 'district' | 'unit';

export interface LeaderPositionEntry {
  position: string; // raw from the report, e.g. "Scoutmaster"
  trained: boolean;
  directContact: boolean; // BSA "direct contact leader" flag
  incompleteCourses: string[]; // course codes still owed
}

export interface UnitLeader {
  tenantId: string;
  id: string;
  memberId: string; // BSA member id — the person's stable identity
  firstName: string;
  lastName: string;
  fullName: string;
  email: string;
  zip: string;
  program: string | null; // "Cub Scouting" / "Scouts BSA" / null
  scope: LeaderScope;
  districtId: string | null; // null only for council scope
  unitId: string | null; // Compass unit doc id, matched by district + name
  unitName: string | null; // raw report name, shown when unmatched
  charterOrg: string | null;
  positions: LeaderPositionEntry[];
  /** Soonest registration expiration across this person's rows. */
  registrationExpires: ISODateString | null;
  /** True if any held position is untrained. */
  anyUntrained: boolean;
  reportDate: ISODateString;
  importedAt: Timestamp;
}

/** One append-only historical observation of a Trained Leaders import —
 *  written alongside the current-state `unitLeaders` wipe+rewrite (see
 *  importTrainedLeaders() in lib/units.ts), but never itself wiped. Exists
 *  so leadership-sustainability questions (additions, departures, role
 *  changes, depth/continuity trends) can be answered later by comparing
 *  snapshots across dates — `unitLeaders` alone only ever shows "right
 *  now."
 *
 *  Deliberately excludes email/phone/zip/address (not needed for
 *  sustainability analysis, and would just be a second, staler copy of
 *  contact data) and `incompleteCourses` (the most granular/sensitive
 *  field in the source report, with no concrete history use identified).
 *  `firstName`/`lastName` ARE kept — without a name, a departed leader
 *  found only by memberId in old history is otherwise unidentifiable to a
 *  human reading it.
 *
 *  One doc per (tenant, snapshotDate, member, scope, district, unit) — the
 *  same grouping importTrainedLeaders() already uses for `unitLeaders`
 *  itself, so a single doc always represents one real leader-position-set
 *  observation, never a partial or merged one. `unitId` is null exactly
 *  when the current-state doc's is: a real, parsed leader-position record
 *  whose unit name doesn't resolve to a Compass Unit — that's still a
 *  genuine historical observation and is never discarded just because the
 *  unit link failed (see the "Leadership History & Current Access
 *  Eligibility Discovery" investigation, Part 3, for why this happens and
 *  what it does and doesn't imply).
 *
 *  MUST NEVER be read by anything that grants or checks Compass access —
 *  see firestore.rules, which gives this collection the same read shape as
 *  unitLeaders (and nothing resembling tenantMemberships/authz) and no
 *  more. Historical leadership state and authorization state are
 *  deliberately, structurally separate; see docs/product-module-
 *  architecture.md's Leadership History section for the full boundary. */
export interface UnitLeaderSnapshot {
  tenantId: string;
  id: string;
  /** The report's own "as of" date (reportDate) — never the wall-clock
   *  import timestamp. One canonical set of docs per (tenant, date); a
   *  same-date re-import fully replaces the prior set for that date (see
   *  importTrainedLeaders()'s delete-before-insert step). */
  snapshotDate: ISODateString;
  /** The triggering unitImports doc id, for provenance. */
  importId: string;
  memberId: string;
  firstName: string;
  lastName: string;
  scope: LeaderScope;
  districtId: string | null;
  unitId: string | null;
  /** Preserved even when unitId is null — the only way to identify which
   *  (possibly unresolved) unit this historical observation belongs to. */
  unitName: string | null;
  /** Position titles only (not the full LeaderPositionEntry shape — no
   *  directContact/incompleteCourses in history). */
  positions: string[];
  /** Subset of positions[] that were trained as of this date. */
  trainedPositions: string[];
  anyUntrained: boolean;
  registrationExpires: ISODateString | null;
  importedAt: Timestamp;
}

export const LEADER_SCOPE_LABELS: Record<LeaderScope, string> = {
  council: 'Council',
  district: 'District',
  unit: 'Unit',
};

// ---- Safeguarding Youth Training (from the "SYT Aging Report") ---------
// One doc per registered adult (by member id) — SYT is person-level, not
// per-position. Joined to `unitLeaders` by memberId for the Leaders view.
// `districtIds` holds every district the person is registered in (empty for
// council-only people) and drives read scoping in firestore.rules.

export interface UnitSyt {
  tenantId: string;
  id: string;
  memberId: string;
  firstName: string;
  lastName: string;
  fullName: string;
  email: string;
  phone: string | null;
  /** SYT currently valid (report's `isyptcurrent2` = YES). */
  isCurrent: boolean;
  /** SYT expiration date — the renewal countdown target. */
  expiresOn: ISODateString | null;
  /** Last SYT completion date. */
  completedOn: ISODateString | null;
  /** Named modules still owed, e.g. ["Bullying", "YPT Certification Test"] —
   *  only populated when SYT is not current. */
  modulesToDo: string[];
  districtIds: string[];
  reportDate: ISODateString;
  importedAt: Timestamp;
}

/** One append-only per-(unit, member) historical SYT observation, written
 *  alongside a SYT import (see importSyt() in lib/units.ts). `UnitSyt`
 *  above is person-level with no `unitId` at all (SYT is a council-wide,
 *  per-adult report, not a per-unit one) and is fully wiped and rewritten
 *  on every import — it alone can never answer "how many of THIS unit's
 *  leaders were SYT-current on a past date."
 *
 *  This collection answers that by resolving each SYT row against the
 *  CURRENT `unitLeaders` roster AT IMPORT TIME — the best available
 *  approximation, since SYT and Trained Leaders are imported independently
 *  and nothing in Compass's data model lets the two be aligned to the same
 *  as-of date (see importSyt()'s own comment for this caveat spelled out
 *  again at the write site). Fanned out to one doc per unit a member
 *  currently holds a UNIT-scoped position at (most members: one row; a
 *  person registered at two units: two rows) — never written for a member
 *  with no current unit-scoped match at all, since a council/district-only
 *  person isn't per-unit data.
 *
 *  No aggregate (current/expiring/expired count) is pre-computed or
 *  stored here — a future reader filters this collection to
 *  (unitId, reportDate) and buckets `isCurrent`/`expiresOn` itself,
 *  exactly the same "raw facts in, derive trends later" shape every other
 *  snapshot collection in this file already uses. Deliberately excludes
 *  `modulesToDo` — the most granular field in the source report, same
 *  reasoning `UnitLeaderSnapshot` already documents for excluding
 *  `incompleteCourses`. */
export interface UnitSytSnapshot {
  tenantId: string;
  id: string;
  unitId: string;
  districtId: string | null;
  reportDate: ISODateString;
  importId: string;
  importedAt: Timestamp;
  memberId: string;
  fullName: string;
  isCurrent: boolean;
  expiresOn: ISODateString | null;
  completedOn: ISODateString | null;
}

/** Normalized match key for a unit name. */
export function normalizeUnitName(name: string): string {
  return name.trim().replace(/\s+/g, ' ').toUpperCase();
}

/** "Pack 0011 (F)" -> 'pack'. */
export function unitTypeFromName(name: string): UnitType {
  const first = name.trim().split(/\s+/)[0]?.toLowerCase() ?? '';
  if (first === 'pack') return 'pack';
  if (first === 'troop') return 'troop';
  if (first === 'crew') return 'crew';
  if (first === 'ship') return 'ship';
  if (first === 'post') return 'post';
  if (first === 'club') return 'club';
  return 'other';
}

/** Report district name ("Pioneer 05") -> a settings.districts id, or null. */
export function matchDistrictId(reportName: string, districts: NamedRecord[]): string | null {
  const cleaned = reportName.replace(/\s+\d+\s*$/, '').trim().toLowerCase();
  return districts.find((d) => d.name.toLowerCase() === cleaned)?.id ?? null;
}

/** MM/DD/YYYY -> YYYY-MM-DD; '' or unparseable -> null. */
export function usDateToIso(s: string): ISODateString | null {
  const m = s.trim().match(/^(\d{1,2})\/(\d{1,2})\/(\d{4})$/);
  if (!m) return null;
  const [, mm, dd, yyyy] = m;
  return `${yyyy}-${mm.padStart(2, '0')}-${dd.padStart(2, '0')}`;
}

/** my.Scouting YPT text -> a status bucket. */
export function yptStatusFromText(raw: string): YptStatus {
  const t = raw.trim().toLowerCase();
  if (t === 'active') return 'active';
  if (t.startsWith('expired')) return 'expired';
  if (t.startsWith('expires')) return 'expiring';
  return 'unknown';
}

/** Drives client-side auth + visibility (src/lib/visibility.ts). NOT a
 *  Firestore doc shape as of Phase 5C — AuthProvider.tsx builds this by
 *  merging the caller's UserIdentity (users/{uid} — email/displayName/
 *  phone/notes, tenant-independent) with their TenantMembership for the
 *  ACTIVE tenant only (role/district/positions/etc.) — see AuthProvider's
 *  buildProfile(). `id` is the uid, not the email: authorization must never
 *  key off email (Phase 5C). For non-admins, `role` / `committees` /
 *  `districtView` are derived from `position` when an admin saves the
 *  membership, so the rules can consume them directly without resolving
 *  the Position list. */
export interface UserProfile {
  id: string; // == uid
  email: string;
  role: UserRole;
  districtId?: string | null;
  /** Position names this user holds (from settings.positions) — a person can
   *  hold more than one ("wears multiple hats"). Empty for no standing seat. */
  positions?: string[];
  /** Stable Permissions V2 assignment IDs when the active membership has been migrated. */
  positionIds?: string[];
  /** Derived: committees this user can see. All committees when district-wide. */
  committees?: string[] | null;
  /** Derived: holds a district-wide Position — may view everything in their
   *  district (and manage the roster), even without edit authority. */
  districtView?: boolean | null;
  /** Derived: holds a Key 3 Position (District Chair/Commissioner/
   *  Executive) — the top non-admin tier. On top of whatever chair/member
   *  access their Position(s) grant, they can create/edit/remove any OTHER
   *  non-admin login in their district (chair or member) — never admin,
   *  which still requires an actual admin via Settings. See
   *  firestore.rules' isKeyThree(). */
  keyThree?: boolean | null;
  /** Legacy council-wide READ flag — superseded by `councilAccess` (Council
   *  Access Phase A). No longer an active authorization source anywhere;
   *  kept only for historical/recovery reference. */
  councilWide?: boolean | null;
  /** Council-level operational access — see TenantMembership.councilAccess's
   *  doc comment in this file for exact semantics. Independent of
   *  `contactAccess` below. */
  councilAccess?: 'dashboardOnly' | 'fullRead' | 'fullRW' | null;
  /** Protected contact-field (email/phone/notes) visibility — see
   *  TenantMembership.contactAccess's doc comment. Independent of
   *  `councilAccess`. */
  contactAccess?: 'none' | 'read' | null;
  /** Optional link to a contacts doc, used for direct task assignment. Compatibility pointer; see personId. */
  contactId?: string | null;
  /** Canonical pointer to this login's Person (tenantPeople), when known. */
  personId?: string | null;
  displayName?: string;
  /** Only used when there's no linked roster Contact (e.g. an admin, who
   *  isn't scoped to a district) — otherwise phone/notes live on the
   *  Contact and these stay null so there's one source of truth. */
  phone?: string | null;
  notes?: string | null;
  createdAt?: Timestamp;
  lastLoginAt?: Timestamp;
}

/** Which seasonal fundraising sale is currently running — admin-set, not
 *  auto-detected from the calendar, since campaign windows shift year to
 *  year. Drives the Dashboard's Seasonal Sale panel (title + which
 *  program's data it would query, once that data model exists). */
export type SeasonalProgram = 'popcorn' | 'campCard';

/** tenants/{tenantId}/settings/app — the authoritative tenant-scoped
 *  application configuration. It is nested under the tenant because this
 *  is a true per-tenant singleton, not a cross-tenant query collection. */
export interface TenantSettings extends AppSettings {
  tenantId: string;
}

/** Shape of application settings after loading the authoritative tenant
 *  singleton. The historical root settings document is not a runtime
 *  configuration source. */
export interface AppSettings {
  districts: NamedRecord[];
  committees: Committee[];
  positions: Position[];
  taskStatuses: StatusRecord[];
  currentProgramYear: string;
  dashboardThresholds: DashboardThresholds;
  activeSeasonalProgram: SeasonalProgram;
}

/** Red/yellow/green cutoffs for the dashboard heatmap + the committee-health
 *  flag list. Each metric: at or above `ok` is green, at or above `warn` is
 *  amber, below `warn` is red. Percentages 0–100. */
export interface DashboardThresholds {
  recharterOkPct: number;
  recharterWarnPct: number;
  sytOkPct: number;
  sytWarnPct: number;
  trainedOkPct: number;
  trainedWarnPct: number;
  /** A committee at or below this fill % is flagged in the short list. */
  committeeFlagPct: number;
}

export const DEFAULT_DASHBOARD_THRESHOLDS: DashboardThresholds = {
  recharterOkPct: 90,
  recharterWarnPct: 75,
  sytOkPct: 90,
  sytWarnPct: 75,
  trainedOkPct: 80,
  trainedWarnPct: 60,
  committeeFlagPct: 50,
};

/** One month's membership totals for a district — written automatically from
 *  each recharter import (summing the unit counts) so a trend line has
 *  history to plot. Doc id is `${districtId}__${month}`; a new import in the
 *  same month overwrites that month. */
export interface MembershipSnapshot {
  tenantId: string;
  id: string;
  districtId: string;
  month: string; // 'YYYY-MM'
  youth: number;
  adults: number;
  units: number;
  source: 'recharterImport' | 'manual';
  reportDate: ISODateString;
  updatedAt?: Timestamp;
}

/** Precomputed dashboard aggregates for one district — written only by the
 *  `recomputeDistrictSummaries` Cloud Function (on every import + nightly), so
 *  the Dashboard never has to scan `unitLeaders` / `unitSyt` to show totals.
 *  Doc id === districtId. The council rollup is these four summed client-side. */
export interface DistrictSummary {
  tenantId: string;
  id: string;
  districtId: string;
  recharter: { chartered: number; soon: number; overdue: number; unknown: number };
  leadershipGaps: number;
  syt: { current: number; total: number };
  trained: { current: number; total: number }; // deduped by member id
  /** Hazardous Weather Training — deduped by member id, canonical evaluator
   *  (functions/src/hazardousWeatherAutomation.ts, mirrors src/lib/
   *  unitHealth.ts). `total` is ONLY leaders for whom it's applicable
   *  (directContact position) — never every registered adult. `current`
   *  counts CURRENT + DUE_SOON (both compliant); MISSING/EXPIRED are the
   *  remainder. NOT_APPLICABLE leaders are excluded from both. */
  weather: { current: number; total: number };
  advancementYtd: { eagle: number; scoutsBsaRanks: number; cubRanks: number };
  advancementPace: { onTrack: number; behind: number; stalled: number; notRated: number };
  membershipLatest: { youth: number; adults: number; units: number; month: string } | null;
  computedAt?: Timestamp;
}

/** Stable id, display name, stable parent id, and Key 3. Committee/lead are
 * configured per council and default off. */
const POSITION_SEED: Array<[
  positionId: string,
  name: string,
  reportsToPositionId: string,
  key3?: boolean,
]> = [
  ['pos_district_chair', 'District Chair', '', true],
  ['pos_district_commissioner', 'District Commissioner', '', true],
  ['pos_district_executive', 'District Executive', '', true],
  ['pos_district_vice_chair', 'District Vice Chair', 'pos_district_chair'],
  ['pos_finance_chair', 'Finance Chair', 'pos_district_chair'],
  ['pos_camping_chair', 'Camping Chair', 'pos_program_chair'],
  ['pos_program_chair', 'Program Chair', 'pos_district_chair'],
  ['pos_activities_chair', 'Activities Chair', 'pos_district_chair'],
  ['pos_training_chair', 'Training Chair', 'pos_program_chair'],
  ['pos_advancement_chair', 'Advancement Chair', 'pos_program_chair'],
  ['pos_membership_chair', 'Membership Chair', 'pos_district_chair'],
  ['pos_member_at_large', 'Member-at-Large', 'pos_district_chair'],
  ['pos_district_volunteer', 'District Volunteer', 'pos_district_chair'],
  ['pos_nominating_chair', 'Nominating Committee Chair', ''],
  ['pos_nominating_committee_member', 'Nominating Committee Member', 'pos_nominating_chair'],
  ['pos_unit_commissioner', 'Unit Commissioner', 'pos_district_commissioner'],
];

export const DEFAULT_POSITIONS: Position[] = POSITION_SEED.map(
  ([positionId, name, reportsToPositionId, key3]) => ({
    positionId,
    name,
    committee: '',
    districtWide: false,
    lead: false,
    reportsToPositionId: reportsToPositionId || null,
    key3: !!key3,
  }),
);

function defaults(): AppSettings {
  return {
    districts: ['Pioneer', 'Sycamore', 'Wanchanagi', 'White Eagle'].map((name) => ({
      id: newId(),
      name,
    })),
    committees: ['Camping', 'Finance', 'Membership', 'Program', 'Training'].map((name) => ({
      id: newId(),
      name,
    })),
    positions: DEFAULT_POSITIONS,
    taskStatuses: [
      { id: newId(), name: 'Not Started', isComplete: false },
      { id: newId(), name: 'In Progress', isComplete: false },
      { id: newId(), name: 'Blocked', isComplete: false },
      { id: newId(), name: 'Done', isComplete: true },
    ],
    currentProgramYear: '2026-2027',
    dashboardThresholds: { ...DEFAULT_DASHBOARD_THRESHOLDS },
    activeSeasonalProgram: 'popcorn',
  };
}

export const DEFAULT_SETTINGS: AppSettings = defaults();

// ---- normalization -------------------------------------------------------
// Tolerates the pre-records shape (plain string[]) so new code can't break on
// data written by the old version; a no-op once the settings doc is migrated.

function asRecord(x: unknown): NamedRecord {
  if (typeof x === 'string') return { id: x, name: x };
  const r = (x ?? {}) as Partial<NamedRecord>;
  return { id: r.id ?? r.name ?? newId(), name: r.name ?? String(r.id ?? '') };
}

function asCommittee(x: unknown): Committee {
  if (typeof x === 'string') return { id: x, name: x, parentId: null };
  const r = (x ?? {}) as Partial<Committee>;
  return {
    ...asRecord(x),
    parentId: r.parentId ?? null,
    // Organization Model Phase C: without this, every committee silently
    // lost its Council/District tag on the way through this normalizer
    // (it only ever built {id, name, parentId}) — every committee read
    // client-side via useSettings() defaulted to 'district' regardless of
    // what was actually stored, even though nothing server-side was ever
    // wrong. See Committee.organizationLevel's own doc comment.
    organizationLevel: r.organizationLevel === 'council' ? 'council' : 'district',
  };
}

function asStatus(x: unknown): StatusRecord {
  if (typeof x === 'string') {
    return { id: x, name: x, isComplete: /^(done|complete|completed)$/i.test(x) };
  }
  const r = (x ?? {}) as Partial<StatusRecord>;
  return { ...asRecord(x), isComplete: !!r.isComplete };
}

export function normalizeSettings(raw: Record<string, unknown> | undefined): AppSettings {
  const d = DEFAULT_SETTINGS;
  const base = raw ?? {};
  const list = (v: unknown, fallback: NamedRecord[]) =>
    Array.isArray(v) && v.length ? (v as unknown[]).map(asRecord) : fallback;
  return {
    districts: list(base.districts, d.districts),
    committees:
      Array.isArray(base.committees) && base.committees.length
        ? (base.committees as unknown[]).map(asCommittee)
        : d.committees,
    positions: Array.isArray(base.positions) ? (base.positions as Position[]) : d.positions,
    taskStatuses:
      Array.isArray(base.taskStatuses) && base.taskStatuses.length
        ? (base.taskStatuses as unknown[]).map(asStatus)
        : d.taskStatuses,
    currentProgramYear:
      typeof base.currentProgramYear === 'string'
        ? base.currentProgramYear
        : d.currentProgramYear,
    dashboardThresholds: {
      ...DEFAULT_DASHBOARD_THRESHOLDS,
      ...(base.dashboardThresholds && typeof base.dashboardThresholds === 'object'
        ? (base.dashboardThresholds as Partial<DashboardThresholds>)
        : {}),
    },
    activeSeasonalProgram:
      base.activeSeasonalProgram === 'popcorn' || base.activeSeasonalProgram === 'campCard'
        ? base.activeSeasonalProgram
        : d.activeSeasonalProgram,
  };
}

/** Resolve a Position name against the configured list. */
export function findPosition(
  positions: Position[] | undefined,
  name: string | null | undefined,
): Position | undefined {
  if (!name) return undefined;
  return (positions ?? []).find((p) => p.name === name);
}

/** Resolve several Position names at once — a person holding multiple
 *  positions. Unknown names (e.g. a deleted Position still on an old record)
 *  are silently dropped. */
export function findPositions(
  allPositions: Position[] | undefined,
  names: string[] | undefined,
): Position[] {
  return (names ?? [])
    .map((n) => findPosition(allPositions, n))
    .filter((p): p is Position => !!p);
}

/** The user/contact-doc fields derived from everything a person holds —
 *  the union across all their positions, expanded to include each held
 *  committee's sub-committees (a Program chair also gets Camping, its
 *  child). `allCommittees` is every committee, used when any held
 *  position is district-wide. Holding zero positions is 'member' with no
 *  committees, same as before.
 *
 *  Council Organization Phase B: `positions` is filtered to district-level
 *  ones ONLY before anything below runs — a council-level Position (see
 *  Position.organizationLevel) never contributes V1 role/committee/
 *  districtView/keyThree authority merely by being held. This mirrors
 *  functions/src/userAccess.ts's deriveTrustedV1Access (the server-side
 *  canonical copy, which filters identically) — real council-Position
 *  authorization comes only through explicit Permissions V2 policy. */
export function deriveAccess(
  positions: Position[],
  allCommittees: Committee[],
): { role: Exclude<UserRole, 'admin'>; committees: string[]; districtView: boolean; keyThree: boolean } {
  const districtPositions = positions.filter((p) => (p.organizationLevel ?? 'district') === 'district');
  if (districtPositions.length === 0) {
    return { role: 'member', committees: [], districtView: false, keyThree: false };
  }
  const districtView = districtPositions.some((p) => p.districtWide);
  const directIds = [...new Set(districtPositions.map((p) => p.committee).filter(Boolean))];
  const committees = districtView
    ? allCommittees.map((c) => c.id)
    : [...new Set(directIds.flatMap((id) => [id, ...committeeDescendants(allCommittees, id)]))];
  return {
    role: districtPositions.some((p) => p.lead) ? 'chair' : 'member',
    committees,
    districtView,
    // Holds a Key 3 Position (District Chair/Commissioner/Executive) — the
    // top permission tier: full control over any other non-admin login in
    // their district, on top of whatever chair/member access their
    // Position(s) otherwise grant. See firestore.rules' isKeyThree().
    keyThree: districtPositions.some((p) => p.key3),
  };
}


// ---- Contacts directory (People) -------------------------------------------
/** A Compass-managed district relationship (District Affiliations). */
export interface DirectoryAffiliation {
  source: 'affiliation' | 'orgChart';
  districtId: string;
  /** Manual affiliations only: the id used to edit or remove it. */
  affiliationId?: string;
  description?: string;
  /** Org Chart affiliations only: the assignment that created it, and the Position. */
  assignmentId?: string;
  positionId?: string;
  positionName?: string;
  addedAt?: string;
}
export interface DirectoryHolding {
  assignmentId: string;
  positionId: string;
  positionName: string;
  organizationLevel: 'district' | 'council';
  districtId: string | null;
  affiliated: boolean;
}

/** Someone who holds a DISTRICT seat from outside that district (listAffiliatedHoldersV1). Name only — registration, home district and
 *  contact details stay in Contacts. They have no roster Contact, so the Org Chart, Dashboard and committee health add them from here. */
export interface AffiliatedHolder {
  assignmentId: string;
  personId: string;
  name: string;
  positionId: string;
  districtId: string;
}

// Every Person Compass knows about is a Contact. Delivered by listDirectoryV1,
// which applies the same Basic/Full visibility rules as the legacy Contact
// delivery; Full-only fields are simply absent for Basic viewers.
export interface DirectoryPerson {
  /** null only for a legacy Contact record not yet linked to a Person. */
  personId: string | null;
  contactId: string | null;
  legacyOnly: boolean;
  ref?: string;
  name: string;
  districtIds: string[];
  positions: string[];
  /** Districts where the Person is officially registered (my.Scouting import only). Optional for a server that predates it. */
  registeredDistrictIds?: string[];
  /** Compass-managed district relationships: manual affiliations and those established by Org Chart assignments. */
  affiliations?: DirectoryAffiliation[];
  /** Organizational Positions with the district of each assignment (null for a council seat). */
  holdings?: DirectoryHolding[];
  /** The roster Contact's OWN district (null = council level or no Contact) and stored Positions — what a roster edit starts from. */
  contactDistrictId?: string | null;
  contactPositions?: string[];
  committees?: string[];
  visibility: 'basic' | 'full';
  email?: string;
  emailSource?: 'manual' | 'imported';
  secondaryEmail?: string;
  /** Further addresses this person is known by (full-visibility viewers only). */
  alternateEmails?: string[];
  phone?: string;
  phoneSource?: 'manual' | 'imported';
  notes?: string;
  /** Admin only: the manually maintained layer, and what the import currently says. */
  manual?: { preferredEmail: string | null; secondaryEmail: string | null; phone: string | null; notes: string | null };
  imported?: { email: string | null; phone: string | null };
}

// ---- Phase 2.3: assigning to People -------------------------------------------------------------------------
/** One selectable Person (from listAssignablePeopleV1). Names, refs and districts only. */
export interface AssignablePerson {
  personId: string;
  name: string;
  ref: string | null;
  districtIds: string[];
  provisional: boolean;
  hasContact: boolean;
  commissionerEligible: boolean;
}
/** A Person as a picker option within ONE district (a Person serving several districts appears once per district). */
export interface PersonOption {
  /** The Person id. */
  id: string;
  name: string;
  ref: string | null;
  districtId: string;
  provisional: boolean;
  commissionerEligible: boolean;
}
